import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../api";

const IntegratedInitialAssessmentForm = ({ formData, setFormData, handleChange, rows, setRows, onSectionSave, savingSection }) => {
    // Read-only Family Meeting Minutes for this case/patient (latest 5) — not stored here.
    const fmmCaseId = formData.case_id || formData.caseId;
    const fmmPatientId = formData.patient_id || formData.patientId;
    const [fmmNotes, setFmmNotes] = useState([]);
    useEffect(() => {
        if (!fmmCaseId && !fmmPatientId) return;
        let active = true;
        api.get("/family-meeting-minutes/for-integrated", { params: { case_id: fmmCaseId, patient_id: fmmPatientId, limit: 5 } })
            .then((res) => { if (active) setFmmNotes(res.data?.notes || []); })
            .catch(() => { if (active) setFmmNotes([]); });
        return () => { active = false; };
    }, [fmmCaseId, fmmPatientId]);
    const addRow = (key) => {
        setFormData(prev => ({
            ...prev,
            [key]: [...(prev[key] || []), { id: Date.now(), problem: "" }]
        }));
    };
    // Generic function to remove a row from a specific list
    const removeRow = (key, id) => {
        const currentList = formData[key] || [];

        // Prevent deletion if only one row remains
        if (currentList.length <= 1) {
            Swal.fire({
                title: "Cannot Delete",
                text: "At least one entry must remain.",
                icon: "warning",
            });
            return;
        }

        Swal.fire({
            title: "Remove this entry?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
        }).then((res) => {
            if (res.isConfirmed) {
                setFormData(prev => ({
                    ...prev,
                    [key]: prev[key].filter((r) => r.id !== id)
                }));
            }
        });
    };

    // Generic function to handle nested array text changes
    const handleArrayChange = (key, id, field, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: prev[key].map((r) => (r.id === id ? { ...r, [field]: value } : r))
        }));
    };
    // Generic function to handle text changes in a repeater row

    const addObjective = () => {
        const newObjective = {
            id: Date.now(),
            name: "",
            obj_status: "Pending",
            obj_date: new Date().toISOString().split('T')[0],
            actions: []
        };

        setFormData(prev => ({
            ...prev,
            objectives: [...(prev.objectives || []), newObjective]
        }));
    };

    const removeObjective = (oIdx) => {
        const currentObjectives = formData.objectives || [];

        // Prevent deletion if only one objective remains
        if (currentObjectives.length <= 1) {
            Swal.fire({
                title: "Cannot Delete",
                text: "At least one objective must remain.",
                icon: "warning",
            });
            return;
        }

        Swal.fire({
            title: "Remove this objective?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
        }).then((res) => {
            if (res.isConfirmed) {
                setFormData(prev => ({
                    ...prev,
                    objectives: prev.objectives.filter((_, index) => index !== oIdx)
                }));
            }
        });
    };

    const updateObjective = (oIdx, field, value) => {
        setFormData(prev => {
            const newObjectives = [...prev.objectives];
            newObjectives[oIdx] = { ...newObjectives[oIdx], [field]: value };
            return { ...prev, objectives: newObjectives };
        });
    };

    const addAction = (oIdx) => {
        const newAction = {
            id: Date.now(),
            action_name: "",
            location: "",
            frequency: "",
            duration: ""
        };

        setFormData(prev => {
            const newObjectives = [...prev.objectives];
            newObjectives[oIdx].actions = [
                ...(newObjectives[oIdx].actions || []),
                newAction
            ];
            return { ...prev, objectives: newObjectives };
        });
    };

    const removeAction = (oIdx, aIdx) => {
        setFormData(prev => {
            const newObjectives = [...prev.objectives];
            newObjectives[oIdx].actions = newObjectives[oIdx].actions.filter((_, index) => index !== aIdx);
            return { ...prev, objectives: newObjectives };
        });
    };

    const updateAction = (oIdx, aIdx, field, value) => {
        setFormData(prev => {
            const newObjectives = [...prev.objectives];
            newObjectives[oIdx].actions[aIdx] = {
                ...newObjectives[oIdx].actions[aIdx],
                [field]: value
            };
            return { ...prev, objectives: newObjectives };
        });
    };

    // Ensure arrays exist to prevent crashes during mapping
    const generalProblems = formData.generalProblems || [];
    const nursingProblems = formData.nursingProblems || [];

    // Section Save Button Component
    const SectionSaveBtn = ({ sectionName }) => (
        <div className="d-flex justify-content-end mt-2 mb-2">
            <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => onSectionSave?.(sectionName)}
                disabled={savingSection === sectionName}
            >
                <i className={`ri-save-2-line me-1 ${savingSection === sectionName ? 'spinner-border spinner-border-sm' : ''}`}></i>
                {savingSection === sectionName ? 'Saving...' : 'Save Section'}
            </button>
        </div>
    );

    // ===== Cognitive Assessment + Discharge Planning (new one-to-one sections) =====
    const cog = formData.cognitive_assessment || {};
    const dp = formData.discharge_planning || {};

    const recomputeCognitiveScores = (c) => {
        const isIntact = (v) => v === "Intact";
        const orientation = ["orientation_time", "orientation_place", "orientation_person"].filter((k) => isIntact(c[k])).length;
        const attention =
            (c.digit_span_forward === "Normal" ? 1 : 0) +
            (c.digit_span_backward === "Normal" ? 1 : 0) +
            (c.serial_7s === "Completed" ? 1 : 0) +
            (c.months_backward === "Completed" ? 1 : 0) +
            (c.distractibility === "Absent" ? 1 : 0);
        const memory =
            (parseInt(c.recall_3_object_immediate) || 0) +
            (parseInt(c.recall_5_min) || 0) +
            (isIntact(c.recent_events_recall) ? 1 : 0) +
            (isIntact(c.personal_history) ? 1 : 0) +
            (isIntact(c.past_events) ? 1 : 0);
        const language =
            (c.fluency === "Normal" ? 1 : 0) +
            (isIntact(c.comprehension) ? 1 : 0) +
            (isIntact(c.naming) ? 1 : 0) +
            (isIntact(c.repetition) ? 1 : 0) +
            (isIntact(c.reading) ? 1 : 0) +
            (isIntact(c.writing) ? 1 : 0);
        const executive =
            (c.proverbs === "Abstract" ? 1 : 0) +
            (isIntact(c.similarities) ? 1 : 0) +
            (isIntact(c.social_judgment) ? 1 : 0) +
            (isIntact(c.test_judgment) ? 1 : 0) +
            (isIntact(c.task_planning) ? 1 : 0);
        const visuospatial =
            (c.shape_copying === "Normal" ? 1 : 0) +
            (c.clock_drawing === "Normal" ? 1 : 0) +
            (isIntact(c.spatial_orientation) ? 1 : 0);
        const total = orientation + attention + memory + language + executive + visuospatial;
        let interpretation = "";
        if (total >= 28) interpretation = "Normal";
        else if (total >= 22) interpretation = "Mild Impairment";
        else if (total >= 15) interpretation = "Moderate Impairment";
        else interpretation = "Severe Impairment";
        return { ...c, orientation_score: orientation, attention_score: attention, memory_score: memory, language_score: language, executive_score: executive, visuospatial_score: visuospatial, total_score: total, interpretation };
    };

    const setCog = (field, value) => {
        setFormData((prev) => {
            const next = { ...(prev.cognitive_assessment || {}), [field]: value };
            return { ...prev, cognitive_assessment: recomputeCognitiveScores(next) };
        });
    };
    const setDp = (field, value) => {
        setFormData((prev) => ({ ...prev, discharge_planning: { ...(prev.discharge_planning || {}), [field]: value } }));
    };
    const csvHas = (v, option) => (v || "").split(",").map((s) => s.trim()).includes(option);
    const toggleCsv = (obj, setter, field, option) => {
        const cur = (obj[field] || "").split(",").map((s) => s.trim()).filter(Boolean);
        const next = cur.includes(option) ? cur.filter((x) => x !== option) : [...cur, option];
        setter(field, next.join(", "));
    };
    // Radio option group for a cognitive field (function call, not a component -> keeps focus)
    const cogOpts = (field, options) => (
        <div className="d-flex flex-wrap gap-3">
            {options.map((opt) => (
                <div className="form-check form-check-inline" key={opt}>
                    <input className="form-check-input" type="radio" name={`cog_${field}`} id={`cog_${field}_${opt}`} checked={cog[field] === opt} onChange={() => setCog(field, opt)} />
                    <label className="form-check-label" htmlFor={`cog_${field}_${opt}`}>{opt}</label>
                </div>
            ))}
        </div>
    );
    const cogRow = (label, field, options) => (
        <div className="row g-2 align-items-center mb-2">
            <div className="col-md-4 fw-semibold small">{label}</div>
            <div className="col-md-8">{cogOpts(field, options)}</div>
        </div>
    );
    const riskCount = ["risk_impulsivity", "risk_poor_judgment", "risk_wandering", "risk_confusion", "risk_non_adherence"].filter((k) => cog[k]).length;

    return (
        <div className="p-3" style={{ maxHeight: "65vh", overflowY: "auto", scrollbarWidth: "thin" }}>
            {/* Client Identification */}
            <div className="accordion" id="integratedAccordion">
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#clientIdentification">
                            Client Identification
                        </button>
                    </h2>
                    <div id="clientIdentification" className="accordion-collapse collapse show">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="mrNumber" value={formData.mrNumber || ""} onChange={handleChange} />
                                        <label>MR #</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="date" className="form-control" name="date" value={formData.date || ""} onChange={handleChange} />
                                        <label>Date</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="time" className="form-control" name="time" value={formData.time || ""} onChange={handleChange} />
                                        <label>Time</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Basic Information" />}
                        </div>
                    </div>
                </div>

                {/* Sociodemographic Detail */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#sociodemographic">
                            Sociodemographic Detail
                        </button>
                    </h2>
                    <div id="sociodemographic" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={formData.name || ""}
                                            onChange={handleChange}
                                            readOnly={!!formData.patientId || !!formData.patient_id}
                                        />
                                        <label>Name</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="age"
                                            value={formData.age || ""}
                                            onChange={handleChange}
                                            readOnly={!!formData.patientId || !!formData.patient_id}
                                        />
                                        <label>Age</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="occupation" value={formData.occupation || ""} onChange={handleChange} />
                                        <label>Occupation</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="gender"
                                            value={formData.gender?.toLowerCase() || ""}
                                            onChange={handleChange}
                                            disabled={!!formData.patientId || !!formData.patient_id}
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                        </select>
                                        <label>Gender</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <select className="form-select" name="maritalStatus" value={formData.maritalStatus || ""} onChange={handleChange}>
                                            <option value="">Select Marital Status</option>
                                            <option value="Single">Single</option>
                                            <option value="Married">Married</option>
                                            <option value="Divorced">Divorced</option>
                                            <option value="Widowed">Widowed</option>
                                        </select>
                                        <label>Marital Status</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="sourceOfReferral" value={formData.sourceOfReferral || ""} onChange={handleChange} />
                                        <label>Source of Referral</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="informant" value={formData.informant || ""} onChange={handleChange} />
                                        <label>Informant (Reliability)</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="comorbid" value={formData.comorbid || ""} onChange={handleChange} />
                                        <label>Co morbid</label>
                                    </div>
                                </div>
                            </div>
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-4">
                                Consent for History and Rehab
                            </small>
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <select
                                        className="form-select"
                                        name="consentType"
                                        value={formData.consentType || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Consent Type</option>
                                        <option value="Client">Client</option>
                                        <option value="Family">Family</option>
                                        <option value="Guardian">Guardian</option>
                                    </select>
                                </div>

                                {(formData.consentType === "Family" || formData.consentType === "Guardian") && (
                                    <div className="col-md-12 mt-3">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="consentReason"
                                                value={formData.consentReason || ""}
                                                onChange={handleChange}
                                                placeholder="Reason"
                                            />
                                            <label>Reason</label>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Sociodemographic Detail" />}
                        </div>
                    </div>
                </div>

                {/* Clinical Findings & Presenting Details */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#clinicalFindings"
                        >
                            Clinical Findings & Presenting Details
                        </button>
                    </h2>

                    <div id="clinicalFindings" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Row 1: Presenting Complaints / Psycho Social Stressors / Functional Adjustment */}
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Presenting Complaints
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="presentingComplaints"
                                            value={formData.presentingComplaints || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Presenting Complaints</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Psycho Social Stressor/s
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="psychoSocialStressors"
                                            value={formData.psychoSocialStressors || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Psycho Social Stressor/s</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Functional / Occupational Adjustment
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="functionalAdjustment"
                                            value={formData.functionalAdjustment || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Functional / Occupational Adjustment</label>
                                    </div>
                                </div>
                            </div>

                            {/* Row 2: Vegetative Functions / Current Treatment / Allergies */}
                            <div className="row g-3 mt-3">
                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Vegetative Functions
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="vegetativeFunctions"
                                            value={formData.vegetativeFunctions || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Vegetative Functions</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Current Treatment
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="currentTreatment"
                                            value={formData.currentTreatment || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Current Treatment</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Allergies
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="allergies"
                                            value={formData.allergies || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Allergies</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Clinical History" />}
                        </div>
                    </div>
                </div>


                {/* Past Psychiatric History */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#pastPsychiatricHistory">
                            Past Psychiatric History
                        </button>
                    </h2>
                    <div id="pastPsychiatricHistory" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-2">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="duration" value={formData.duration || ""} onChange={handleChange} />
                                        <label>Duration</label>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="episodes" value={formData.episodes || ""} onChange={handleChange} />
                                        <label>Episodes</label>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="ect" value={formData.ect || ""} onChange={handleChange} />
                                        <label>ECT</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="course" value={formData.course || ""} onChange={handleChange} />
                                        <label>Course</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="compliance" value={formData.compliance || ""} onChange={handleChange} />
                                        <label>Compliance</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="admission" value={formData.admission || ""} onChange={handleChange} />
                                        <label>Admission</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="opdFU" value={formData.opdFU || ""} onChange={handleChange} />
                                        <label>OPD F/U</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="lai" value={formData.lai || ""} onChange={handleChange} />
                                        <label>LAI</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="epsSideEffects" value={formData.epsSideEffects || ""} onChange={handleChange} />
                                        <label>EPS/Severe Side Effects</label>
                                    </div>
                                </div>

                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Past Psychiatric History" />}
                        </div>
                    </div>
                </div>

                {/* Risk Assessment as per SOP’s */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#riskAssessment">
                            Risk Assessment as per SOP’s
                        </button>
                    </h2>
                    <div id="riskAssessment" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Risk Assessment Items */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">
                                Risk Assessment (Low / Medium / High)
                            </small>
                            <div className="row g-3 mb-3">
                                {[
                                    {
                                        name: "roh",
                                        label: "ROH – Risk of Homicidal",
                                    },
                                    {
                                        name: "ros",
                                        label: "ROS – Risk of Suicide",
                                    },
                                    {
                                        name: "rov",
                                        label: "ROV – Risk of Violence",
                                    },
                                    {
                                        name: "roe",
                                        label: "ROE – Risk of Elopement",
                                    },
                                    {
                                        name: "rof",
                                        label: "ROF – Risk of Falls",
                                    },
                                    {
                                        name: "rovu",
                                        label: "ROVU – Risk of Vulnerability",
                                    },
                                ].map((item, index) => (
                                    <div className="col-md-4 mb-3" key={index}>
                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                            {item.label}
                                        </small>

                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Risk Level</option>
                                            <option value="Low">Low (Green)</option>
                                            <option value="Medium">Medium (Yellow)</option>
                                            <option value="High">High (Red)</option>
                                        </select>
                                    </div>
                                ))}
                            </div>

                            {/* Separate Detail Forms */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">
                                Separate Detail Forms
                            </small>
                            <div className="row g-3 mb-3">
                                {[
                                    { name: "suicideFormNursing", label: "Client at risk of suicide, separate form to be completed by doctors and nursing staff" },
                                    { name: "violenceFormNursing", label: "Client at risk of violence, separate form to be completed by doctors and nursing staff" },
                                ].map((item) => (
                                    <div className="col-md-6" key={item.name}>
                                        <label className="form-label fw-semibold">{item.label}</label>
                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select</option>
                                            <option value="Low">Low</option>
                                            <option value="Medium">Medium</option>
                                            <option value="High">High</option>
                                        </select>
                                    </div>
                                ))}
                            </div>

                            {/* Safety Care Plan */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">
                                Safety Care Plan Form
                            </small>
                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-semibold">
                                    To be given to clients at the time of discharge (D.C)
                                </label>
                                <select
                                    className="form-select"
                                    name="safetyCarePlanNursing"
                                    value={formData.safetyCarePlanNursing || ""}
                                    onChange={handleChange}
                                >
                                    <option value="">Select</option>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </select>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Risk Assessment" />}
                        </div>
                    </div>

                </div>

                {/* Background & History Details */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#backgroundHistory"
                        >
                            Background & History Details
                        </button>
                    </h2>

                    <div id="backgroundHistory" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Row 1: Medical / Surgical History • Educational History • Personal History */}
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Medical / Surgical History (Any Investigations)
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="medicalSurgicalHistory"
                                            value={formData.medicalSurgicalHistory || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Medical / Surgical History</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Personal History
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="personalHistory"
                                            value={formData.personalHistory || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Personal History</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Educational History
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="educationalHistory"
                                            value={formData.educationalHistory || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Educational History</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Occupational History
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="occupationalHistory"
                                            value={formData.occupationalHistory || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Occupational History</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Spiritual History
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="spiritualHistory"
                                            value={formData.spiritualHistory || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Spiritual History</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Marital History
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="maritalHistory"
                                            value={formData.maritalHistory || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Marital History</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Legal Issues
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="legalIssues"
                                            value={formData.legalIssues || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Legal Issues</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Pre-Morbid Personality
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="preMorbidPersonality"
                                            value={formData.preMorbidPersonality || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Pre-Morbid Personality</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Substance Use
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="substanceUse"
                                            value={formData.substanceUse || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Substance Use</label>
                                    </div>
                                </div>

                                {/* Row 4: Substance Use (alone, centered) */}
                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Family History
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="familyHistory"
                                            value={formData.familyHistory || ""}
                                            onChange={handleChange}
                                            rows="4"
                                        />
                                        <label>Family History</label>
                                    </div>
                                </div>

                                {/* Family History Checkboxes (kept as is) */}
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-4">
                                        Family of Origin
                                    </small>

                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="familyOrigin"
                                            value="Hx of Psychiatric Illness"
                                            checked={formData.familyOrigin.includes("Hx of Psychiatric Illness")}
                                            onChange={handleChange}
                                            id="familyPsychIllness"
                                        />
                                        <label className="form-check-label" htmlFor="familyPsychIllness">
                                            Hx of Psychiatric Illness
                                        </label>
                                    </div>

                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="familyOrigin"
                                            value="Hx of Consanguinous Marriage of Client"
                                            checked={formData.familyOrigin.includes("Hx of Consanguinous Marriage of Client")}
                                            onChange={handleChange}
                                            id="familyConsanguinous"
                                        />
                                        <label className="form-check-label" htmlFor="familyConsanguinous">
                                            Hx of Consanguinous Marriage of Client
                                        </label>
                                    </div>

                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="familyOrigin"
                                            value="Hx of Suicide in Family"
                                            checked={formData.familyOrigin.includes("Hx of Suicide in Family")}
                                            onChange={handleChange}
                                            id="familySuicide"
                                        />
                                        <label className="form-check-label" htmlFor="familySuicide">
                                            Hx of Suicide in Family
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Background & History Details" />}
                        </div>
                    </div>
                </div>


                {/* General Physical Examination */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#generalPhysical">
                            General Physical Examination
                        </button>
                    </h2>
                    <div id="generalPhysical" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3 row">
                            <div className="form-floating form-floating-outline mb-3">
                                <textarea className="form-control" name="generalAppearance" value={formData.generalAppearance || ""} onChange={handleChange} rows="2" />
                                <label>General Appearance</label>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" name="cranialNerve" value={formData.cranialNerve || ""} onChange={handleChange} />
                                    <label>Cranial Nerve</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" name="sensorySystem" value={formData.sensorySystem || ""} onChange={handleChange} />
                                    <label>Sensory System</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" name="motorSystem" value={formData.motorSystem || ""} onChange={handleChange} />
                                    <label>Motor System</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" name="meningealIrritation" value={formData.meningealIrritation || ""} onChange={handleChange} />
                                    <label>Signs of Meningeal Irritation</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" name="cerebellarSigns" value={formData.cerebellarSigns || ""} onChange={handleChange} />
                                    <label>Cerebellar Signs</label>
                                </div>
                            </div>
                            {/* Physical Signs */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#physicalSigns">
                                        Physical Signs
                                    </button>
                                </h2>
                                <div id="physicalSigns" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="row">

                                            {[
                                                "pallor",
                                                "jaundice",
                                                "cyanosis",
                                                "cutMarks",
                                                "clubing",
                                                "thyroid",
                                                "lymphNodes",
                                                "burnMarks",
                                                "dehydration",
                                                "edema",
                                                "abnormalMovementOrTremors"
                                            ].map((item, index) => (
                                                <div className="col-md-4 mb-3" key={index}>
                                                    <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                                        {item
                                                            .replace(/([A-Z])/g, " $1")
                                                            .replace(/^./, (s) => s.toUpperCase())}
                                                    </small>

                                                    <select
                                                        className="form-select"
                                                        name={item}
                                                        value={formData[item] || ""}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="Yes">Yes</option>
                                                        <option value="No">No</option>
                                                    </select>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {onSectionSave && <SectionSaveBtn sectionName="General Physical Examination" />}
                                </div>
                            </div>
                            {/* Central Nervous System */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#centralNervous">
                                        Central Nervous System
                                    </button>
                                </h2>
                                <div id="centralNervous" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <table className="table">
                                            <thead >
                                                <tr >
                                                    <th colSpan="2" className="text-center" style={{ background: "transparent" }}>
                                                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block">
                                                            Right
                                                        </small>
                                                    </th>
                                                    <th colSpan="2" className="text-center" style={{ background: "transparent" }}>
                                                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block">
                                                            Left
                                                        </small>
                                                    </th>
                                                </tr>

                                                <tr>
                                                    <th className="text-center" style={{ background: "transparent" }}>
                                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                                            Upper Limb
                                                        </small>
                                                    </th>
                                                    <th className="text-center" style={{ background: "transparent" }}>
                                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                                            Lower Limb
                                                        </small>
                                                    </th>
                                                    <th className="text-center" style={{ background: "transparent" }}>
                                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                                            Upper Limb
                                                        </small>
                                                    </th>
                                                    <th className="text-center" style={{ background: "transparent" }}>
                                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                                            Lower Limb
                                                        </small>
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>

                                                {/* BULK */}
                                                <tr>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="bulkRightUpper"
                                                                value={formData.bulkRightUpper || ""} onChange={handleChange} />
                                                            <label>BULK</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="bulkRightLower"
                                                                value={formData.bulkRightLower || ""} onChange={handleChange} />
                                                            <label>BULK</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="bulkLeftUpper"
                                                                value={formData.bulkLeftUpper || ""} onChange={handleChange} />
                                                            <label>BULK</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="bulkLeftLower"
                                                                value={formData.bulkLeftLower || ""} onChange={handleChange} />
                                                            <label>BULK</label>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* TONE */}
                                                <tr>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="toneRightUpper"
                                                                value={formData.toneRightUpper || ""} onChange={handleChange} />
                                                            <label>TONE</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="toneRightLower"
                                                                value={formData.toneRightLower || ""} onChange={handleChange} />
                                                            <label>TONE</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="toneLeftUpper"
                                                                value={formData.toneLeftUpper || ""} onChange={handleChange} />
                                                            <label>TONE</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="toneLeftLower"
                                                                value={formData.toneLeftLower || ""} onChange={handleChange} />
                                                            <label>TONE</label>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* POWER */}
                                                <tr>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="powerRightUpper"
                                                                value={formData.powerRightUpper || ""} onChange={handleChange} />
                                                            <label>POWER</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="powerRightLower"
                                                                value={formData.powerRightLower || ""} onChange={handleChange} />
                                                            <label>POWER</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="powerLeftUpper"
                                                                value={formData.powerLeftUpper || ""} onChange={handleChange} />
                                                            <label>POWER</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="powerLeftLower"
                                                                value={formData.powerLeftLower || ""} onChange={handleChange} />
                                                            <label>POWER</label>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* REFLEXES */}
                                                <tr>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="reflexesRightUpper"
                                                                value={formData.reflexesRightUpper || ""} onChange={handleChange} />
                                                            <label>REFLEXES</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="reflexesRightLower"
                                                                value={formData.reflexesRightLower || ""} onChange={handleChange} />
                                                            <label>REFLEXES</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="reflexesLeftUpper"
                                                                value={formData.reflexesLeftUpper || ""} onChange={handleChange} />
                                                            <label>REFLEXES</label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="form-floating form-floating-outline mb-4">
                                                            <input className="form-control" name="reflexesLeftLower"
                                                                value={formData.reflexesLeftLower || ""} onChange={handleChange} />
                                                            <label>REFLEXES</label>
                                                        </div>
                                                    </td>
                                                </tr>

                                            </tbody>
                                        </table>

                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Physical Examination" />}
                        </div>
                    </div>
                </div>


                {/* Systemic Examination (Combined Section) */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#systemicExamination">
                            Systemic Examination
                        </button>
                    </h2>

                    <div id="systemicExamination" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Respiratory System */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                Respiratory System
                            </small>

                            <div className="row g-3 mb-4">
                                {[
                                    { name: "respiratoryInspection", label: "Inspection" },
                                    { name: "respiratoryPalpation", label: "Palpation" },
                                    { name: "respiratoryPercussion", label: "Percussion" },
                                    { name: "respiratoryAuscultation", label: "Auscultation" }
                                ].map((item, index) => (
                                    <div className="col-md-3" key={index}>
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name={item.name}
                                                value={formData[item.name] || ""}
                                                onChange={handleChange}
                                            />
                                            <label>{item.label}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Abdominal Examination */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                Abdominal Examination
                            </small>

                            <div className="row g-3 mb-4">
                                {[
                                    { name: "abdominalInspection", label: "Inspection" },
                                    { name: "abdominalPalpation", label: "Palpation" },
                                    { name: "abdominalPercussion", label: "Percussion" },
                                    { name: "abdominalAuscultation", label: "Auscultation" }
                                ].map((item, index) => (
                                    <div className="col-md-3" key={index}>
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name={item.name}
                                                value={formData[item.name] || ""}
                                                onChange={handleChange}
                                            />
                                            <label>{item.label}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Cardiovascular System */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                Cardiovascular System
                            </small>

                            <div className="row g-3 mb-4">
                                {[
                                    { name: "cardioInspection", label: "Inspection" },
                                    { name: "cardioPalpation", label: "Palpation" },
                                    { name: "cardioPercussion", label: "Percussion" },
                                    { name: "cardioAuscultation", label: "Auscultation" }
                                ].map((item, index) => (
                                    <div className="col-md-3" key={index}>
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name={item.name}
                                                value={formData[item.name] || ""}
                                                onChange={handleChange}
                                            />
                                            <label>{item.label}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* EPS */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                EPS
                            </small>

                            <div className="row g-3">
                                {[
                                    { name: "akathisia", label: "Akathisia" },
                                    { name: "dystonia", label: "Dystonia" },
                                    { name: "td", label: "TD" },
                                    { name: "parkinsons", label: "Parkinson’s" }
                                ].map((item, index) => (
                                    <div className="col-md-3" key={index}>
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name={item.name}
                                                value={formData[item.name] || ""}
                                                onChange={handleChange}
                                            />
                                            <label>{item.label}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Systemic Examination" />}
                        </div>
                    </div>
                </div>


                {/* Diagnosis Section (Combined) */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#diagnosisSection"
                        >
                            Diagnosis Details
                        </button>
                    </h2>

                    <div id="diagnosisSection" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            <div className="row">

                                {/* Provisional Diagnosis */}
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Provisional Diagnosis (ICD-II, DSM-V)
                                    </small>
                                    <div className="form-floating form-floating-outline mb-3">
                                        <textarea
                                            className="form-control"
                                            name="provisionalDiagnosis"
                                            value={formData.provisionalDiagnosis || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Provisional Diagnosis</label>
                                    </div>
                                </div>

                                {/* Differential Diagnosis */}
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Differential Diagnosis
                                    </small>
                                    <div className="form-floating form-floating-outline mb-3">
                                        <textarea
                                            className="form-control"
                                            name="differentialDiagnosis"
                                            value={formData.differentialDiagnosis || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Differential Diagnosis</label>
                                    </div>
                                </div>

                                {/* Details about diagnosis & treatment */}
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Details about diagnosis & treatment explanation
                                    </small>
                                    <div className="form-floating form-floating-outline mb-3">
                                        <textarea
                                            className="form-control"
                                            name="diagnosisTreatmentDetails"
                                            value={formData.diagnosisTreatmentDetails || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Details</label>
                                    </div>
                                </div>

                            </div>

                            {/* Consent taken from */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#consentTaken">
                                        Consent taken from
                                    </button>
                                </h2>
                                <div id="consentTaken" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="checkbox" name="consentTakenClient" checked={formData.consentTakenClient} onChange={handleChange} id="consentTakenClient" />
                                                    <label className="form-check-label" htmlFor="consentTakenClient">Client</label>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="checkbox" name="consentTakenFamily" checked={formData.consentTakenFamily} onChange={handleChange} id="consentTakenFamily" />
                                                    <label className="form-check-label" htmlFor="consentTakenFamily">Family</label>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="checkbox" name="consentTakenGuardian" checked={formData.consentTakenGuardian} onChange={handleChange} id="consentTakenGuardian" />
                                                    <label className="form-check-label" htmlFor="consentTakenGuardian">Guardian</label>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="form-floating form-floating-outline">
                                                    <input type="text" className="form-control" name="consentTakenTerm" value={formData.consentTakenTerm || ""} onChange={handleChange} />
                                                    <label>List term in case Client cant give answer</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Diagnosis Section" />}
                        </div>
                    </div>
                </div>



                {/* Management Plan */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#managementPlan">
                            Management Plan
                        </button>
                    </h2>
                    <div id="managementPlan" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="form-floating form-floating-outline mb-3">
                                <input type="text" className="form-control" name="approxLengthOfStay" value={formData.approxLengthOfStay || ""} onChange={handleChange} />
                                <label>Approximate Length of Stay</label>
                            </div>
                            {/* --- GENERAL PROBLEM LIST --- */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#problemList">
                                        Problem List (Medical/General)
                                    </button>
                                </h2>
                                <div id="problemList" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        {generalProblems.map((row) => (
                                            <div key={row.id} className="border rounded-4 p-3 mb-3 shadow-sm bg-light-subtle">
                                                <div className="row g-3 align-items-center">
                                                    <div className="col-md-10">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Problem"
                                                            value={row.problem}
                                                            onChange={(e) => handleArrayChange("generalProblems", row.id, "problem", e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-md-2 text-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            onClick={() => removeRow("generalProblems", row.id)}
                                                        >
                                                            <i className="ri-delete-bin-line"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="text-end mt-3">
                                            <button type="button" className="btn btn-outline-primary" onClick={() => addRow("generalProblems")}>
                                                <i className="ri-add-line"></i> Add Problem
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Treatment Plan (Combined) */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button fw-semibold collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#treatmentPlanSection"
                                    >
                                        Treatment Plan
                                    </button>
                                </h2>

                                <div id="treatmentPlanSection" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">

                                        <div className="row">

                                            {/* Diagnostic Workup */}
                                            <div className="col-md-12">
                                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                                    Diagnostic Workup
                                                </small>
                                                <div className="form-floating form-floating-outline mb-3">
                                                    <textarea
                                                        className="form-control"
                                                        name="diagnosticWorkup"
                                                        value={formData.diagnosticWorkup || ""}
                                                        onChange={handleChange}
                                                        rows="3"
                                                    />
                                                    <label>Diagnostic Workup</label>
                                                </div>
                                            </div>

                                            {/* Pharmacotherapy */}
                                            <div className="col-md-12">
                                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                                    Pharmacotherapy
                                                </small>
                                                <div className="form-floating form-floating-outline mb-3">
                                                    <textarea
                                                        className="form-control"
                                                        name="pharmacotherapy"
                                                        value={formData.pharmacotherapy || ""}
                                                        onChange={handleChange}
                                                        rows="3"
                                                    />
                                                    <label>Pharmacotherapy</label>
                                                </div>
                                            </div>

                                            {/* Others */}
                                            <div className="col-md-12">
                                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                                    Others (ECT, Psychotherapy, Milieu Therapy, ADLs, Occupational Therapy)
                                                </small>
                                                <div className="form-floating form-floating-outline mb-3">
                                                    <textarea
                                                        className="form-control"
                                                        name="treatmentOthers"
                                                        value={formData.treatmentOthers || ""}
                                                        onChange={handleChange}
                                                        rows="3"
                                                    />
                                                    <label>Others</label>
                                                </div>
                                            </div>

                                        </div>
                                        {onSectionSave && <SectionSaveBtn sectionName="Treatment Plan" />}
                                    </div>

                                </div>
                            </div>

                            {/* Resident & Consultant Details (Combined) */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button fw-semibold collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#residentConsultantSection"
                                    >
                                        Resident & Consultant Details
                                    </button>
                                </h2>

                                <div id="residentConsultantSection" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        {/* consultants */}
                                        <div className="row">
                                            <div className="col-md-12">
                                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                                    Consultants
                                                </small>
                                                <div className="form-floating form-floating-outline mb-3">
                                                    <textarea
                                                        className="form-control"
                                                        name="consultants"
                                                        value={formData.consultants || ""}
                                                        onChange={handleChange}
                                                        rows="3"
                                                    />
                                                    <label>Consultants</label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Resident Details */}
                                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                            Resident Details
                                        </small>

                                        <div className="row g-3 mb-4">
                                            <div className="col-md-4">
                                                <div className="form-floating form-floating-outline">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="residentName"
                                                        value={formData.residentName || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>Resident Name</label>
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="form-floating form-floating-outline">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="cellNumber"
                                                        value={formData.cellNumber || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>Cell #</label>
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="form-floating form-floating-outline">
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="residentSignDate"
                                                        value={formData.residentSignDate || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>Date</label>
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="form-floating form-floating-outline">
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        name="residentTime"
                                                        value={formData.residentTime || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>Time</label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Consultant Details */}
                                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                            Consultant’s Details
                                        </small>

                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="form-floating form-floating-outline">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="doctorsName"
                                                        value={formData.doctorsName || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>Doctor’s Name</label>
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="form-floating form-floating-outline">
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="consultantSignDate"
                                                        value={formData.consultantSignDate || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>Date</label>
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="form-floating form-floating-outline">
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        name="consultantTime"
                                                        value={formData.consultantTime || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>Time</label>
                                                </div>
                                            </div>
                                        </div>
                                        {onSectionSave && <SectionSaveBtn sectionName="Resident & Consultant Details" />}
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Management Plan" />}
                        </div>
                    </div>
                </div>




                {/* Nursing Assessment */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#nursingAssessment">
                            Nursing Assessment
                        </button>
                    </h2>
                    <div id="nursingAssessment" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="unit" value={formData.unit || ""} onChange={handleChange} />
                                        <label>Unit</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="date" className="form-control" name="dateOfAdmission" value={formData.dateOfAdmission || ""} onChange={handleChange} />
                                        <label>Date of Admission</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="time" className="form-control" name="timeOfAdmission" value={formData.timeOfAdmission || ""} onChange={handleChange} />
                                        <label>Time</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <select className="form-select" name="typeOfAdmission" value={formData.typeOfAdmission || ""} onChange={handleChange}>
                                            <option value="">Select</option>
                                            <option value="Elective">Elective</option>
                                            <option value="Emergency">Emergency</option>
                                        </select>
                                        <label>Type of Admission</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <select className="form-select" name="modeOfTransportation" value={formData.modeOfTransportation || ""} onChange={handleChange}>
                                            <option value="">Select</option>
                                            <option value="Wheel Chair">Wheel Chair</option>
                                            <option value="Stretcher">Stretcher</option>
                                            <option value="Ambulatory">Ambulatory</option>
                                            <option value="Others">Others</option>
                                        </select>
                                        <label>Mode of Transportation</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="accompaniedBy" value={formData.accompaniedBy || ""} onChange={handleChange} />
                                        <label>Accompanied by</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="idBand" value={formData.idBand || ""} onChange={handleChange} />
                                        <label>ID Band</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="date" className="form-control" name="nursingAge" value={formData.nursingAge || ""} onChange={handleChange} />
                                        <label>DOB</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="nursingWeight" value={formData.nursingWeight || ""} onChange={handleChange} />
                                        <label>Weight/KG</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="nursingHeight" value={formData.nursingHeight || ""} onChange={handleChange} />
                                        <label>Height/cm</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="bloodPressure" value={formData.bloodPressure || ""} onChange={handleChange} />
                                        <label>Blood Pressure/mmhg</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="temperature" value={formData.temperature || ""} onChange={handleChange} />
                                        <label>Temperature/°C</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="pulse" value={formData.pulse || ""} onChange={handleChange} />
                                        <label>Pulse/bpm</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="respRate" value={formData.respRate || ""} onChange={handleChange} />
                                        <label>Resp Rate/mm</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="o2Saturation" value={formData.o2Saturation || ""} onChange={handleChange} />
                                        <label>O2 Saturation/%</label>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control" name="reasonForAdmission" value={formData.reasonForAdmission || ""} onChange={handleChange} rows="3" />
                                        <label>Reason for Admission</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Nursing Assessment" />}
                        </div>
                    </div>
                </div>
                {/* ================= COMMUNICABLE / ISOLATION / BELONGINGS ================= */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#communicableDisease"
                        >
                            Patient Safety & Infection Control
                        </button>
                    </h2>

                    <div id="communicableDisease" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* ================= Communicable Disease ================= */}
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-3 py-1">
                                        Communicable Disease
                                    </small>
                                </div>
                            </div>

                            <div className="row">
                                {[
                                    "None",
                                    "Cholera",
                                    "Active TB",
                                    "Meningitis",
                                    "Influenza",
                                    "Measles",
                                    "Chicken Pox",
                                    "Others"
                                ].map((disease) => (
                                    <div key={disease} className="col-md-3 mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.communicableDisease?.includes(disease)}
                                                onChange={(e) => {
                                                    const updated = e.target.checked
                                                        ? [...(formData.communicableDisease || []), disease]
                                                        : formData.communicableDisease.filter(d => d !== disease);

                                                    handleChange({
                                                        target: {
                                                            name: "communicableDisease",
                                                            value: updated
                                                        }
                                                    });
                                                }}
                                            />
                                            <label className="form-check-label fw-semibold">
                                                {disease}
                                            </label>
                                        </div>

                                        {(disease === "None" || disease === "Others") &&
                                            formData.communicableDisease?.includes(disease) && (
                                                <input
                                                    type="text"
                                                    className="form-control mt-2"
                                                    placeholder={`Specify ${disease}`}
                                                    value={formData[`communicable_${disease}`] || ""}
                                                    onChange={handleChange}
                                                    name={`communicable_${disease}`}
                                                />
                                            )}
                                    </div>
                                ))}
                            </div>

                            <hr className="my-4" />

                            {/* ================= Isolation Type ================= */}
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-3 py-1">
                                        Isolation Type
                                    </small>
                                </div>
                            </div>

                            <div className="row">
                                {[
                                    "Not Applicable",
                                    "Standard",
                                    "Contact",
                                    "Droplet",
                                    "Air borne",
                                    "Others"
                                ].map((type) => (
                                    <div key={type} className="col-md-4 mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="isolationType"
                                                value={type}
                                                checked={formData.isolationType === type}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label fw-semibold">
                                                {type}
                                            </label>
                                        </div>

                                        {type === "Others" && formData.isolationType === "Others" && (
                                            <input
                                                type="text"
                                                className="form-control mt-2"
                                                placeholder="Specify Isolation Type"
                                                name="isolationOther"
                                                value={formData.isolationOther || ""}
                                                onChange={handleChange}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <hr className="my-4" />

                            {/* ================= Personal Belongings ================= */}
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-3 py-1">
                                        Personal Belongings / Valuables
                                    </small>
                                </div>
                            </div>

                            {[
                                { label: "Eye Glasses / Contact Lenses", name: "eyeGlasses" },
                                { label: "Hearing Aids", name: "hearingAids" },
                                { label: "Dentures", name: "dentures" },
                                { label: "Jewelry / Expensive Item", name: "jewelry" },
                                { label: "Others", name: "personalOthers" },
                            ].map((item) => (
                                <div key={item.name} className="row align-items-center mb-3">
                                    <div className="col-md-5 fw-semibold">
                                        {item.label}
                                    </div>
                                    <div className="col-md-7">
                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select</option>
                                            <option value="Kept at bedside on client request">
                                                Kept at bedside on client request
                                            </option>
                                            <option value="Sent home with">
                                                Sent home with
                                            </option>
                                            <option value="Handed over to">
                                                Handed over to
                                            </option>
                                            <option value="Not applicable">
                                                Not applicable
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            ))}
                            {onSectionSave && <SectionSaveBtn sectionName="Communicable Disease" />}
                        </div>
                    </div>
                </div>

                {/* Multi Drug Resistant Organism */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#mdrOrganism"
                        >
                            Multi Drug Resistant Organism
                        </button>
                    </h2>

                    <div id="mdrOrganism" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                {[
                                    "MDR Acinetobacter",
                                    "MDR Pseudomonas",
                                    "MRSA",
                                    "VRE",
                                    "ESBL",
                                    "None",
                                ].map((item, i) => (
                                    <div className="col-md-4" key={i}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.mdrOrganisms?.includes(item)}
                                                onChange={(e) => {
                                                    let updated = formData.mdrOrganisms
                                                        ? [...formData.mdrOrganisms]
                                                        : [];

                                                    if (e.target.checked) {
                                                        updated.push(item);
                                                    } else {
                                                        updated = updated.filter(
                                                            (val) => val !== item
                                                        );
                                                    }

                                                    setFormData({
                                                        ...formData,
                                                        mdrOrganisms: updated,
                                                    });
                                                }}
                                                id={`mdr_${i}`}
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor={`mdr_${i}`}
                                            >
                                                {item}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Valuable Policy */}
                            <div className="col-md-12 mt-3">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                    Patient / Family informed about hospital’s Patient Valuable Policy
                                </small>
                                <div className="form-floating form-floating-outline mb-3">
                                    <textarea
                                        className="form-control"
                                        name="informedValuablePolicy"
                                        value={formData.informedValuablePolicy || ""}
                                        onChange={handleChange}
                                        rows="3"
                                    />
                                    <label>Valuable Policy</label>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Multi Drug Resistant Organism" />}
                        </div>
                    </div>
                </div>

                {/* Pain Assessment */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#painAssessment"
                        >
                            Pain Assessment
                        </button>
                    </h2>

                    <div id="painAssessment" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                        Presence of Pain: Are you currently having pain?
                                    </small>
                                    <select
                                        className="form-select"
                                        name="presenceOfPain"
                                        value={formData.presenceOfPain || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Option</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>

                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                        Pain score / intensity (0-10)
                                    </small>
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="painScore"
                                            value={formData.painScore || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Pain score / intensity (0-10)</label>
                                    </div>
                                </div>

                                {/* Pain Scale Used */}
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                        Pain Scale Used
                                    </small>

                                    <div className="row">
                                        {[
                                            "Verbal Numeric Pain (VNP)",
                                            "Face Pain Rating (FPS)",
                                            "Behavioural Scale",
                                        ].map((scale, i) => (
                                            <div className="col-md-4" key={i}>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={formData.painScaleUsed?.includes(scale)}
                                                        onChange={(e) => {
                                                            let updated = formData.painScaleUsed
                                                                ? [...formData.painScaleUsed]
                                                                : [];

                                                            if (e.target.checked) {
                                                                updated.push(scale);
                                                            } else {
                                                                updated = updated.filter(
                                                                    (item) => item !== scale
                                                                );
                                                            }

                                                            setFormData({
                                                                ...formData,
                                                                painScaleUsed: updated,
                                                            });
                                                        }}
                                                        id={`painScale_${i}`}
                                                    />
                                                    <label
                                                        className="form-check-label"
                                                        htmlFor={`painScale_${i}`}
                                                    >
                                                        {scale}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="painLocation"
                                            value={formData.painLocation || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Location (Primary site)</label>
                                    </div>
                                </div>
                            </div>

                            <small>If pain score is greater than 4, refer Pain Flow Sheet</small>
                            {onSectionSave && <SectionSaveBtn sectionName="Pain Assessment" />}
                        </div>
                    </div>
                </div>

                {/* Functional Assessment */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#functionalAssessmentMain"
                        >
                            Functional Assessment
                        </button>
                    </h2>

                    <div id="functionalAssessmentMain" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Nested Accordion: Cognition / Perception */}
                            <div className="accordion" id="cognitionAccordion">
                                <div className="accordion-item mb-3 shadow-sm rounded">
                                    <h2 className="accordion-header">
                                        <button
                                            className="accordion-button fw-semibold collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#cognitionCollapse"
                                        >
                                            Cognition / Perception Pattern
                                        </button>
                                    </h2>
                                    <div id="cognitionCollapse" className="accordion-collapse collapse">
                                        <div className="accordion-body pt-3">

                                            <div className="row g-3">

                                                {/* Level of Conscious */}
                                                <div className="col-md-4">
                                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2">
                                                        Level of Conscious
                                                    </small>
                                                    <select
                                                        className="form-select"
                                                        name="levelOfConscious"
                                                        value={formData.levelOfConscious || ""}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Option</option>
                                                        {["Oriented x3", "Alert", "Disoriented", "Drowsy", "Confused", "Un response"].map((option, idx) => (
                                                            <option key={idx} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Sensory */}
                                                <div className="col-md-4">
                                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">
                                                        Sensory
                                                    </small>
                                                    <select
                                                        className="form-select"
                                                        name="sensory"
                                                        value={formData.sensory || ""}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Option</option>
                                                        {["Asymptomatic", "Numbness", "Paralysis", "Tingling", "Others"].map((option, idx) => (
                                                            <option key={idx} value={option}>{option}</option>
                                                        ))}
                                                    </select>

                                                    {formData.sensory === "Others" && (
                                                        <input
                                                            type="text"
                                                            className="form-control mt-2"
                                                            name="sensoryOthers"
                                                            value={formData.sensoryOthers || ""}
                                                            onChange={handleChange}
                                                            placeholder="Specify"
                                                        />
                                                    )}
                                                </div>

                                                {/* Vision */}
                                                <div className="col-md-4">
                                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">Vision</small>
                                                    <select
                                                        className="form-select"
                                                        name="vision"
                                                        value={formData.vision || ""}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Option</option>
                                                        {["Normal", "Impaired", "Blind", "Others"].map((opt, i) => (
                                                            <option key={i} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>

                                                    {formData.vision === "Others" && (
                                                        <input
                                                            type="text"
                                                            className="form-control mt-2"
                                                            name="visionOthers"
                                                            value={formData.visionOthers || ""}
                                                            onChange={handleChange}
                                                            placeholder="Specify"
                                                        />
                                                    )}
                                                </div>

                                                {/* Hearing */}
                                                <div className="col-md-4">
                                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">Hearing</small>
                                                    <select
                                                        className="form-select"
                                                        name="hearing"
                                                        value={formData.hearing || ""}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Option</option>
                                                        {["Normal", "Impaired", "Deaf", "Others"].map((opt, i) => (
                                                            <option key={i} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>

                                                    {formData.hearing === "Others" && (
                                                        <input
                                                            type="text"
                                                            className="form-control mt-2"
                                                            name="hearingOthers"
                                                            value={formData.hearingOthers || ""}
                                                            onChange={handleChange}
                                                            placeholder="Specify"
                                                        />
                                                    )}
                                                </div>

                                                {/* Speech */}
                                                <div className="col-md-4">
                                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">Speech</small>
                                                    <select
                                                        className="form-select"
                                                        name="speech"
                                                        value={formData.speech || ""}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Option</option>
                                                        {["Normal", "Slurred", "Aphasic", "Tracheotomy", "Others"].map((opt, i) => (
                                                            <option key={i} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>

                                                    {formData.speech === "Others" && (
                                                        <input
                                                            type="text"
                                                            className="form-control mt-2"
                                                            name="speechOthers"
                                                            value={formData.speechOthers || ""}
                                                            onChange={handleChange}
                                                            placeholder="Specify"
                                                        />
                                                    )}
                                                </div>

                                            </div>
                                            {onSectionSave && <SectionSaveBtn sectionName="Cognition / Perception Pattern" />}
                                        </div>

                                    </div>
                                </div>
                            </div>

                            {/* Self Caring (Checkboxes combined into one field) */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-3 px-2">Self Caring</small>
                            <div className="row g-2 mb-3">
                                {["Feeding", "Hygiene", "Toileting", "Ambulation", "Others"].map((task, i) => (
                                    <div className="col-md-4" key={i}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name={`selfCaring_${task}`}
                                                checked={formData.selfCaring?.includes(task)}
                                                onChange={(e) => {
                                                    let updated = formData.selfCaring ? [...formData.selfCaring] : [];
                                                    if (e.target.checked) {
                                                        updated.push(task);
                                                    } else {
                                                        updated = updated.filter((item) => item !== task);
                                                    }
                                                    setFormData({
                                                        ...formData,
                                                        selfCaring: updated,
                                                    });
                                                }}
                                                id={`selfCaring_${task}`}
                                            />
                                            <label className="form-check-label" htmlFor={`selfCaring_${task}`}>{task}</label>
                                        </div>
                                        {task === "Others" && formData.selfCaring?.includes("Others") && (
                                            <input
                                                type="text"
                                                className="form-control mt-2"
                                                name="selfCaringOther"
                                                value={formData.selfCaringOther || ""}
                                                onChange={handleChange}
                                                placeholder="Specify"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Musculoskeletal */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-3 px-2">
                                Musculoskeletal
                            </small>

                            <div className="row g-2 mb-3">
                                {[
                                    "No Problem Identified",
                                    "Deformities",
                                    "Contractures",
                                    "Amputed",
                                    "Bedridden",
                                    "Others",
                                ].map((item, i) => (
                                    <div className="col-md-4" key={i}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.musculoskeletal?.includes(item)}
                                                onChange={(e) => {
                                                    let updated = formData.musculoskeletal
                                                        ? [...formData.musculoskeletal]
                                                        : [];

                                                    if (e.target.checked) {
                                                        updated.push(item);
                                                    } else {
                                                        updated = updated.filter((val) => val !== item);
                                                    }

                                                    setFormData({
                                                        ...formData,
                                                        musculoskeletal: updated,
                                                    });
                                                }}
                                                id={`musculoskeletal_${i}`}
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor={`musculoskeletal_${i}`}
                                            >
                                                {item}
                                            </label>
                                        </div>

                                        {item === "Others" &&
                                            formData.musculoskeletal?.includes("Others") && (
                                                <input
                                                    type="text"
                                                    className="form-control mt-2"
                                                    name="musculoskeletalOther"
                                                    value={formData.musculoskeletalOther || ""}
                                                    onChange={handleChange}
                                                    placeholder="Specify"
                                                />
                                            )}
                                    </div>
                                ))}
                            </div>

                            {/* Use of Assisting Equipment */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-3 px-2">
                                Use of Assisting Equipment
                            </small>

                            <div className="row g-2 mb-3">
                                {[
                                    "None",
                                    "Walker",
                                    "WheelChair",
                                    "Transfer Devices",
                                    "Bathing Devices",
                                    "Crutches",
                                    "Others",
                                ].map((item, i) => (
                                    <div className="col-md-4" key={i}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.assisting?.includes(item)}
                                                onChange={(e) => {
                                                    let updated = formData.assisting
                                                        ? [...formData.assisting]
                                                        : [];

                                                    if (e.target.checked) {
                                                        updated.push(item);
                                                    } else {
                                                        updated = updated.filter((val) => val !== item);
                                                    }

                                                    setFormData({
                                                        ...formData,
                                                        assisting: updated,
                                                    });
                                                }}
                                                id={`assisting_${i}`}
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor={`assisting_${i}`}
                                            >
                                                {item}
                                            </label>
                                        </div>

                                        {item === "Others" &&
                                            formData.assisting?.includes("Others") && (
                                                <input
                                                    type="text"
                                                    className="form-control mt-2"
                                                    name="assistingOther"
                                                    value={formData.assistingOther || ""}
                                                    onChange={handleChange}
                                                    placeholder="Specify"
                                                />
                                            )}
                                    </div>
                                ))}
                            </div>


                            <p className="text-muted mt-3">
                                Note: Any gray area checked requires Physician to be notified for relevant referral
                            </p>
                            {onSectionSave && <SectionSaveBtn sectionName="Pain Assessment" />}
                        </div>

                    </div>
                </div>

                {/* Orientation to Unit */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#orientationUnit"
                        >
                            Orientation to Unit
                        </button>
                    </h2>
                    <div id="orientationUnit" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Items Given on Admission */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2">
                                Items Provided on Admission
                            </small>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {[
                                    "Hospital Dress",
                                    "Blanket",
                                    "Admission Pack",
                                    "Information Booklet",
                                    "Bed Allocation",
                                ].map((item, i) => (
                                    <div className="col-md-2 form-check" key={i}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={formData.admissionItems?.includes(item)}
                                            onChange={(e) => {
                                                let updated = formData.admissionItems
                                                    ? [...formData.admissionItems]
                                                    : [];

                                                if (e.target.checked) {
                                                    updated.push(item);
                                                } else {
                                                    updated = updated.filter(
                                                        (val) => val !== item
                                                    );
                                                }

                                                setFormData({
                                                    ...formData,
                                                    admissionItems: updated,
                                                });
                                            }}
                                            id={`admission_${i}`}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`admission_${i}`}
                                        >
                                            {item}
                                        </label>
                                    </div>
                                ))}
                            </div>

                            {/* Cognition / Perception Pattern */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2">
                                Cognition / Perception Pattern
                            </small>

                            <div className="row mb-3">
                                {[
                                    "Behaviour with others",
                                    "Telephone",
                                    "Bed Controls",
                                    "Bathroom",
                                    "Dining Area",
                                    "TV",
                                    "Lounge",
                                    "Play Area",
                                    "Occupational Therapy Room",
                                    "Hospital Valuable Policy",
                                    "Client Routine Responsibilities",
                                    "Smoking Policy",
                                    "Client Own Medicine Policy",
                                    "Visitor Hours",
                                    "No Food / Fruit Allowed",
                                    "Meal Time",
                                    "Safety & Security Checks",
                                    "Prohibited Items",
                                    "Prayer Area"
                                ].map((item, i) => (
                                    <div className="col-md-4 mb-2" key={i}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`cognition_${i}`}
                                                checked={formData.cognition?.includes(item)}
                                                onChange={(e) => {
                                                    let updated = formData.cognition
                                                        ? [...formData.cognition]
                                                        : [];

                                                    if (e.target.checked) {
                                                        updated.push(item);
                                                    } else {
                                                        updated = updated.filter((val) => val !== item);
                                                    }

                                                    setFormData({
                                                        ...formData,
                                                        cognition: updated,
                                                    });
                                                }}
                                            />
                                            <label
                                                className="form-check-label cursor-pointer"
                                                htmlFor={`cognition_${i}`}
                                            >
                                                {item}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>


                            {/* Client Level as Dropdown */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2">
                                Client Level
                            </small>
                            <div className="mb-3 col-md-12">
                                <select
                                    className="form-select"
                                    name="clientLevel"
                                    value={formData.clientLevel || ""}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Level</option>
                                    {["A", "B", "C", "D", "Others"].map((level) => (
                                        <option key={level} value={level}>{level}</option>
                                    ))}
                                </select>
                                {formData.clientLevel === "Others" && (
                                    <input
                                        type="text"
                                        className="form-control mt-2"
                                        name="clientLevelOthers"
                                        value={formData.clientLevelOthers || ""}
                                        onChange={handleChange}
                                        placeholder="Specify"
                                    />
                                )}
                            </div>

                            {/* Orientation Check Questions as Dropdown */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2">
                                Orientation Check Questions
                            </small>
                            <div className="row g-3 mb-3">
                                {[
                                    { name: "roomAllocation", label: "Room Allocation and Bed allotment, water cooler" },
                                    { name: "informMedicine", label: "Inform Client about medicine administration & lab testing" },
                                    { name: "guideFamilyMeetings", label: "Guide about family meetings & call schedules" },
                                    { name: "infoConsultants", label: "Provide info about consultant doctors" },
                                    { name: "authorizeHairCutting", label: "Authorize hair cutting for hygiene/lice" },
                                    { name: "isolationContagious", label: "Maintain isolation for contagious disease" },
                                ].map((item) => (
                                    <div className="col-md-6" key={item.name}>
                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block my-3 px-1">{item.label}</small>
                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Orientation to Unit" />}
                        </div>

                    </div>
                </div>

                {/* Nutrition Assessment & Dietitian Referral */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#nutritionDietitian"
                        >
                            Nutrition Assessment & Dietitian Referral
                        </button>
                    </h2>
                    <div id="nutritionDietitian" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Basic Info */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="weightKg"
                                            value={formData.weightKg || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Weight (Kg)</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="heightCm"
                                            value={formData.heightCm || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Height (cm)</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="bmi"
                                            value={formData.bmi || ""}
                                            onChange={handleChange}
                                        />
                                        <label>BMI</label>
                                    </div>
                                </div>
                            </div>

                            {/* Nutrition Risk Questions as Dropdowns */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-3 px-2">
                                Nutrition Risk Questions
                            </small>
                            <div className="row g-3 mb-3">
                                {[
                                    { name: "bmiLess18", label: "BMI ≤ 18.5" },
                                    { name: "bmiGreater25", label: "BMI ≥ 25 (Obesity ASIAN Classification)" },
                                    { name: "lostWeight3Months", label: "Weight loss in last 3 months" },
                                    { name: "recentHospitalization", label: "Recent hospitalization requiring intensive care" },
                                ].map((item) => (
                                    <div className="col-md-6" key={item.name}>
                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block my-3 px-2">
                                            {item.label}
                                        </small>
                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                ))}
                            </div>

                            {/* Medical Conditions / Dietitian Referral */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-3 px-2">
                                Mandatory Dietitian Referral Conditions
                            </small>

                            <div className="row g-3 mb-3">
                                {[
                                    "Chronic Obstructive Pulmonary Disease (COPD)",
                                    "Major abdominal surgery involving GI tract",
                                    "Inflammatory bowel disease (IBD)",
                                    "Chronic Kidney Disease (CKD-ESRD)",
                                    "Tube Feeding",
                                    "Cirrhosis",
                                    "Bedsores",
                                    "Cancer",
                                ].map((item, i) => (
                                    <div className="col-md-3" key={i}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.dietitianReferralConditions?.includes(item)}
                                                onChange={(e) => {
                                                    let updated = formData.dietitianReferralConditions
                                                        ? [...formData.dietitianReferralConditions]
                                                        : [];

                                                    if (e.target.checked) {
                                                        updated.push(item);
                                                    } else {
                                                        updated = updated.filter(
                                                            (val) => val !== item
                                                        );
                                                    }

                                                    setFormData({
                                                        ...formData,
                                                        dietitianReferralConditions: updated,
                                                    });
                                                }}
                                                id={`dietitian_${i}`}
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor={`dietitian_${i}`}
                                            >
                                                {item}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>


                            {/* Referral Notes */}
                            <div className="form-floating form-floating-outline mt-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    name="referDietitian"
                                    value={formData.referDietitian || ""}
                                    onChange={handleChange}
                                />
                                <label>Refer to Dietitian / Notes</label>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Nutrition Assessment & Dietitian Referral" />}
                        </div>
                    </div>
                </div>


                {/* Risk Assessment & Safety Forms (Nursing) */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button fw-semibold collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#riskSafetyNursing"
                        >
                            Risk Assessment & Safety Forms (Nursing)
                        </button>
                    </h2>
                    <div id="riskSafetyNursing" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            {/* Risk Assessment Items */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">
                                Risk Assessment (Low / Medium / High)
                            </small>
                            <div className="row g-3 mb-3">
                                {[
                                    { name: "rohNursing", label: "ROH: Risk of Homicidal (risk of harming others)" },
                                    { name: "rosNursing", label: "ROS: Risk of Suicide (risk of self-harm)" },
                                    { name: "rovNursing", label: "ROV: Risk of Violence (risk of harming self or others)" },
                                    { name: "roeNursing", label: "ROE: Risk of Elopement (leaving hospital without permission)" },
                                    { name: "rofNursing", label: "ROF: Risk of Falls (physical harm due to falls)" },
                                    { name: "rovuNursing", label: "ROVU: Risk of Vulnerability (exploitation or harm due to vulnerable state)" },
                                ].map((item) => (
                                    <div className="col-md-4" key={item.name}>
                                        <label className="form-label fw-semibold">{item.label}</label>
                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select</option>
                                            <option value="Low">Low</option>
                                            <option value="Medium">Medium</option>
                                            <option value="High">High</option>
                                        </select>
                                    </div>
                                ))}
                            </div>

                            {/* Separate Detail Forms */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">
                                Separate Detail Forms
                            </small>
                            <div className="row g-3 mb-3">
                                {[
                                    { name: "suicideFormNursing2", label: "Client at risk of suicide, separate form to be completed by doctors and nursing staff" },
                                    { name: "violenceFormNursing2", label: "Client at risk of violence, separate form to be completed by doctors and nursing staff" },
                                ].map((item) => (
                                    <div className="col-md-6" key={item.name}>
                                        <label className="form-label fw-semibold">{item.label}</label>
                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select</option>
                                            <option value="Low">Low</option>
                                            <option value="Medium">Medium</option>
                                            <option value="High">High</option>
                                        </select>
                                    </div>
                                ))}
                            </div>

                            {/* Safety Care Plan */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-2 py-1">
                                Safety Care Plan Form
                            </small>
                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-semibold">
                                    To be given to clients at the time of discharge (D.C)
                                </label>
                                <select
                                    className="form-select"
                                    name="safetyCarePlanNursing2"
                                    value={formData.safetyCarePlanNursing2 || ""}
                                    onChange={handleChange}
                                >
                                    <option value="">Select</option>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </select>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Risk Assessment & Safety Forms (Nursing)" />}
                        </div>
                    </div>
                </div>


                {/* Mental State Examination / MSE */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#mentalStateExamination">
                            Mental State Examination / MSE
                        </button>
                    </h2>
                    <div id="mentalStateExamination" className="accordion-collapse collapse p-3">
                        <div className="row g-3 mb-2">
                            {[
                                { name: "appearance", label: "Appearance", options: ["Average height", "Tall", "Short", "Average weight", "Lean", "Healthy", "Obese", "According to stated age", "Appears younger", "Appears Older", "Unkempt", "Appropriate dressing", "Inappropriate dressing"] },
                                { name: "behavior", label: "Behavior", options: ["Calm and Relax", "Co operative", "Guarded", "Pre occupied", "Tearful", "Distracted", "Withdrawn", "Normal Eye Contact", "Staring", "Avoiding eye contact", "Stent pies", "Self mumbling", "Self laughing", "Catatonia", "Mannerism", "Disinhibited", "Compulsive behavior", "Echolalia / Echopraxia"] },
                                { name: "speechMse", label: "Speech", options: ["Clear", "Relevant", "Irrelevant", "Rapid", "Slow", "Loud", "Soft", "Paucity", "Muteness", "Excessive", "Pressured", "Slurred", "Stammer", "Mumbled"] },
                                { name: "moodAffect", label: "Mood / Affect", options: ["Euthymic", "Happy", "Sad", "Elated", "Labile", "Anxious", "Flat", "Blunted", "Anhedonia", "Congruent", "Incongruent"] },
                                { name: "thoughtProcess", label: "Thought Process", options: ["Goal directed", "Tangential", "Circumstantial", "Flight of Ideas", "Neologism", "Echolalia", "De-realization", "Looseness of association", "Thought blocking", "Word salad"] },
                                { name: "thoughtContent", label: "Thought Content", options: ["Delusion", "Persecutory", "Grandiose", "Religious Delusion", "Somatic Delusion", "Delusion of Guilt", "Obsession", "Ideas of reference", "Delusion of infidelity", "Hypochondriasis", "Magical thinking", "Phobias", "Ideas of being controlled", "Nihilistic", "Thought insertion", "Thought withdrawal", "Thought Broadcasting", "Verbatim"] },
                                { name: "insight", label: "Insight", options: ["Intact", "Partial", "Absent"] },
                                { name: "memory", label: "Memory", options: ["Recent", "Remote", "Immediate"] },
                                { name: "selfEsteem", label: "Self Esteem", options: ["High", "Low", "Normal"] },
                                { name: "hopelessness", label: "Hopelessness", options: ["Present", "Absent"] },
                                { name: "worthlessness", label: "Worthlessness", options: ["Present", "Absent"] },
                                { name: "coping", label: "Coping", options: ["Effective", "Ineffective"] },
                            ].map((item) => (
                                <div className="col-md-4 p-2" key={item.name}>
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-2 px-2">
                                        {item.label}
                                    </small>
                                    <select
                                        className="form-select"
                                        name={item.name}
                                        value={formData[item.name] || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Option</option>
                                        {item.options.map((opt) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>
                        {onSectionSave && <SectionSaveBtn sectionName="Mental State Examination / MSE" />}
                    </div>


                </div>

                {/* Socioeconomic */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#socioeconomic">
                            Socioeconomic
                        </button>
                    </h2>
                    <div id="socioeconomic" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                {/* Marital Status */}
                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">Marital Status</small>
                                    <select
                                        className="form-select"
                                        name="maritalStatusSocio"
                                        value={formData.maritalStatusSocio || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="Single">Single</option>
                                        <option value="Married">Married</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">No of Children</small>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="noOfChildren"
                                        value={formData.noOfChildren || ""}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>


                            <div className="row g-3 mt-3">

                                {/* Living Situation */}
                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Living Situation
                                    </small>

                                    <select
                                        className="form-select"
                                        name="livingSituation"
                                        value={formData.livingSituation || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Option</option>
                                        <option value="Alone">Staying alone</option>
                                        <option value="Spouse">Spouse</option>
                                        <option value="Children">Children</option>
                                        <option value="Friends">Friends</option>
                                        <option value="Relatives">Relatives</option>
                                        <option value="Maid">Maid</option>
                                        <option value="Others">Others</option>
                                    </select>

                                    {formData.livingSituation === "Others" && (
                                        <input
                                            type="text"
                                            className="form-control mt-2"
                                            name="livingSituationSpecify"
                                            value={formData.livingSituationSpecify || ""}
                                            onChange={handleChange}
                                            placeholder="Specify living situation"
                                        />
                                    )}
                                </div>

                                {/* Work Situation */}
                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Work Situation
                                    </small>

                                    <select
                                        className="form-select"
                                        name="workSituation"
                                        value={formData.workSituation || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Option</option>
                                        <option value="Employed">Employed</option>
                                        <option value="Unemployed">Unemployed</option>
                                        <option value="Retired">Retired</option>
                                    </select>

                                    {formData.workSituation === "Employed" && (
                                        <input
                                            type="text"
                                            className="form-control mt-2"
                                            name="occupationSocio"
                                            value={formData.occupationSocio || ""}
                                            onChange={handleChange}
                                            placeholder="Occupation"
                                        />
                                    )}
                                </div>

                                {/* Habits */}
                                <div className="col-md-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                        Habits
                                    </small>

                                    <select
                                        className="form-select"
                                        name="habit"
                                        value={formData.habit || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Option</option>
                                        <option value="Smoking">Smoking</option>
                                        <option value="Alcohol">Alcohol</option>
                                        <option value="PanTobacco">Pan / Tobacco</option>
                                        <option value="Others">Others</option>
                                    </select>

                                    {formData.habit === "Others" && (
                                        <input
                                            type="text"
                                            className="form-control mt-2"
                                            name="habitSpecify"
                                            value={formData.habitSpecify || ""}
                                            onChange={handleChange}
                                            placeholder="Specify habit"
                                        />
                                    )}
                                </div>

                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Socioeconomic" />}
                        </div>
                    </div>
                </div>

                {/* Religious / Culture /Spiritual Needs */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#religiousNeeds">
                            Religious / Culture /Spiritual Needs
                        </button>
                    </h2>
                    <div id="religiousNeeds" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="accordion-body pt-3">
                                <div className="row g-3">
                                    {/* Religion */}
                                    <div className="col-md-6">
                                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">Religion</small>
                                        <select
                                            className="form-select"
                                            name="religion"
                                            value={formData.religion || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Religion</option>
                                            <option value="Islam">Islam</option>
                                            <option value="Christianity">Christianity</option>
                                            <option value="Hinduism">Hinduism</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {formData.religion === "Other" && (
                                            <input
                                                type="text"
                                                className="form-control mt-2"
                                                name="religionOther"
                                                value={formData.religionOther || ""}
                                                onChange={handleChange}
                                                placeholder="Specify"
                                            />
                                        )}
                                    </div>


                                    {/* Religious/Cultural Practices */}
                                    <div className="col-md-6">
                                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                            Are there any religious, cultural practices that need to be a part of your care?
                                        </small>
                                        <select
                                            className="form-select"
                                            name="religiousPractices"
                                            value={formData.religiousPractices || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Conditional Inputs for Practices */}
                                {formData.religiousPractices === "Yes" && (
                                    <div className="row g-3 mt-2">
                                        <div className="col-md-3">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="privacy"
                                                    value={formData.privacy || ""}
                                                    onChange={handleChange}
                                                />
                                                <label>Privacy</label>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="food"
                                                    value={formData.food || ""}
                                                    onChange={handleChange}
                                                />
                                                <label>Food</label>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="genderRegalia"
                                                    value={formData.genderRegalia || ""}
                                                    onChange={handleChange}
                                                />
                                                <label>Gender Regalia</label>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="otherValues"
                                                    value={formData.otherValues || ""}
                                                    onChange={handleChange}
                                                />
                                                <label>Any other values</label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Religious / Culture /Spiritual Needs" />}

                        </div>

                    </div>
                </div>

                {/* Review of Systems */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#reviewOfSystems">
                            Review of Systems
                        </button>
                    </h2>
                    <div id="reviewOfSystems" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* Oral Section */}
                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">Oral</small>

                            {/* Mouth / Tongue */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="mouthTongue"
                                            value={formData.mouthTongue || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Normal">Normal</option>
                                            <option value="Moist">Moist</option>
                                            <option value="Coated">Coated</option>
                                            <option value="Ulcerated">Ulcerated</option>
                                        </select>
                                        <label>Mouth / Tongue</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="oralSignSymptom"
                                            value={formData.oralSignSymptom || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Asymptomatic">Asymptomatic</option>
                                            <option value="Nausea">Nausea</option>
                                            <option value="Vomiting">Vomiting</option>
                                            <option value="Chewing / Swallowing Difficulty">Chewing / Swallowing Difficulty</option>
                                            <option value="Hematemesis">Hematemesis</option>
                                            <option value="Others">Others</option>
                                        </select>
                                        <label>Sign / Symptom</label>
                                    </div>
                                    {formData.oralSignSymptom === "Others" && (
                                        <input
                                            type="text"
                                            className="form-control mt-2"
                                            name="oralSignSymptomOthers"
                                            value={formData.oralSignSymptomOthers || ""}
                                            onChange={handleChange}
                                            placeholder="Specify"
                                        />
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="ngGastric"
                                            value={formData.ngGastric || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">NG / Gastric tube</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    {formData.ngGastric === "Yes" && (
                                        <div className="form-floating form-floating-outline mt-2">
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="ngGastricDate"
                                                value={formData.ngGastricDate || ""}
                                                onChange={handleChange}
                                            />
                                            <label>Date</label>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Skin Section */}
                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">Skin</small>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="skinAppearance"
                                            value={formData.skinAppearance || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Appearance</option>
                                            <option value="Normal">Normal</option>
                                            <option value="Pale">Pale</option>
                                            <option value="Flushed">Flushed</option>
                                            <option value="Jaundice">Jaundice</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <label>Appearance</label>
                                    </div>
                                    {formData.skinAppearance === "Other" && (
                                        <input
                                            type="text"
                                            className="form-control mt-2"
                                            name="skinAppearanceOther"
                                            value={formData.skinAppearanceOther || ""}
                                            onChange={handleChange}
                                            placeholder="Specify"
                                        />
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="skinTemperature"
                                            value={formData.skinTemperature || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Temperature</option>
                                            <option value="Normal">Normal</option>
                                            <option value="Warm">Warm</option>
                                            <option value="Cool">Cool</option>
                                        </select>
                                        <label>Temperature</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="skinContagious"
                                            value={formData.skinContagious || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Contagious</option>
                                            <option value="Non Contagious">Non Contagious</option>
                                            <option value="Other Ulceration">Other Ulceration</option>
                                        </select>
                                        <label>Contagious</label>
                                    </div>
                                </div>

                                {formData.skinContagious === "Other Ulceration" && (
                                    <>
                                        <div className="col-md-6">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="skinLocation"
                                                    value={formData.skinLocation || ""}
                                                    onChange={handleChange}
                                                />
                                                <label>Location</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="skinReasons"
                                                    value={formData.skinReasons || ""}
                                                    onChange={handleChange}
                                                />
                                                <label>Reasons</label>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">Cardiovascular</small>
                            {/* Cardio Section */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="cardioPulse"
                                            value={formData.cardioPulse || ""}
                                            onChange={handleChange}
                                        >
                                            {/* <option value="">Pulse</option> */}
                                            <option value="Regular">Regular</option>
                                            <option value="Irregular">Irregular</option>
                                        </select>
                                        <label>Pulse</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="cardioSignSymptom"
                                            value={formData.cardioSignSymptom || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Sign / Symptom</option>
                                            <option value="Asymptomatic">Asymptomatic</option>
                                            <option value="Chest Pain">Chest Pain</option>
                                            <option value="Cyanosis Chest tightness">Cyanosis Chest tightness</option>
                                            <option value="Ghabrahat">Ghabrahat</option>
                                            <option value="Calf Pain">Calf Pain</option>
                                            <option value="Edema">Edema</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <label>Sign / Symptom</label>
                                    </div>
                                    {formData.cardioSignSymptom === "Other" && (
                                        <input
                                            type="text"
                                            className="form-control mt-2"
                                            name="cardioSignSymptomOther"
                                            value={formData.cardioSignSymptomOther || ""}
                                            onChange={handleChange}
                                            placeholder="Specify"
                                        />
                                    )}
                                </div>

                                <div className="row g-3 mb-3">

                                    {/* Respiratory */}
                                    <div className="col-md-6">
                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                            Respiratory
                                        </small>

                                        <div className="form-floating form-floating-outline">
                                            <select
                                                className="form-select"
                                                name="respiration"
                                                value={formData.respiration || ""}
                                                onChange={handleChange}
                                            >
                                                <option value="">Respiration</option>
                                                <option value="Regular">Regular</option>
                                                <option value="Irregular">Irregular</option>
                                                <option value="SOB">SOB</option>
                                                <option value="Chest Pain">Chest Pain</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            <label>Respiration</label>
                                        </div>

                                        {formData.respiration === "Other" && (
                                            <input
                                                type="text"
                                                className="form-control mt-2"
                                                name="respirationOther"
                                                value={formData.respirationOther || ""}
                                                onChange={handleChange}
                                                placeholder="Specify"
                                            />
                                        )}
                                    </div>

                                    {/* Cough */}
                                    <div className="col-md-6">
                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">
                                            Cough
                                        </small>

                                        <div className="form-floating form-floating-outline">
                                            <select
                                                className="form-select"
                                                name="cough"
                                                value={formData.cough || ""}
                                                onChange={handleChange}
                                            >
                                                <option value="">Cough</option>
                                                <option value="Productive">Productive</option>
                                                <option value="Non Productive">Non Productive</option>
                                                <option value="Heamoptysis">Heamoptysis</option>
                                                <option value="O2 Saturation">O2 Saturation</option>
                                            </select>
                                            <label>Cough</label>
                                        </div>

                                        {formData.cough === "Productive" && (
                                            <input
                                                type="text"
                                                className="form-control mt-2"
                                                name="coughOther"
                                                value={formData.coughOther || ""}
                                                onChange={handleChange}
                                                placeholder="Specify"
                                            />
                                        )}
                                    </div>

                                </div>
                            </div>

                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">Elimination</small>
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">Bowel</small>
                            {/* Bowel Section */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="bowelMovement"
                                            value={formData.bowelMovement || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Bowel Movement</option>
                                            <option value="Normal">Normal</option>
                                            <option value="Constipation">Constipation</option>
                                            <option value="Diarrhea">Diarrhea</option>
                                        </select>
                                        <label>Bowel Movement</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="bowelSignSymptom"
                                            value={formData.bowelSignSymptom || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Sign / Symptom</option>
                                            <option value="Nausea Vomiting">Nausea Vomiting</option>
                                            <option value="Indigestion">Indigestion</option>
                                            <option value="Stool continence Malaria">Stool continence Malaria</option>
                                        </select>
                                        <label>Sign / Symptom</label>
                                    </div>
                                </div>
                            </div>

                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">Urinary</small>
                            {/* Urinary Section */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="urinaryAppearance"
                                            value={formData.urinaryAppearance || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Appearance</option>
                                            <option value="Clear Cloudy Heamaturia">Clear Cloudy Heamaturia</option>
                                        </select>
                                        <label>Appearance</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="urinarySignSymptom"
                                            value={formData.urinarySignSymptom || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Sign / Symptom</option>
                                            <option value="Asymptomatic">Asymptomatic</option>
                                            <option value="Burning sensation">Burning sensation</option>
                                            <option value="Dribbling">Dribbling</option>
                                            <option value="Frequency">Frequency</option>
                                            <option value="Incontinence">Incontinence</option>
                                            <option value="Retention">Retention</option>
                                            <option value="Urgency">Urgency</option>
                                        </select>
                                        <label>Sign / Symptom</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="dateOfInsertion"
                                            value={formData.dateOfInsertion || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Date of Insertion</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="catheterType"
                                            value={formData.catheterType || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Catheter Type</label>
                                    </div>
                                </div>

                                <div className="col-md-8">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="urinaryOthers"
                                            value={formData.urinaryOthers || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Others</label>
                                    </div>
                                </div>
                            </div>

                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-3">Obs / Gyn</small>
                            {/* Obs / Gyn Section */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="obsGyn"
                                            value={formData.obsGyn || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Obs / Gyn</option>
                                            <option value="Not Applicable">Not Applicable</option>
                                        </select>
                                        <label>Obs / Gyn</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="menstrualCycle"
                                            value={formData.menstrualCycle || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Menstrual Cycle</option>
                                            <option value="Regular">Regular</option>
                                            <option value="Irregular">Irregular</option>
                                        </select>
                                        <label>Menstrual Cycle</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="dysmenorrhea"
                                            value={formData.dysmenorrhea || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Dysmenorrhea</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                        <label>Dysmenorrhea</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="skinChanges"
                                            value={formData.skinChanges || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Skin Changes</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="obsGynOthers"
                                            value={formData.obsGynOthers || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Others (Specify)</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Review of Systems" />}
                        </div>
                    </div>
                </div>

                {/* --- SECTION 2: NURSING PROBLEM LIST --- */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#problemListNursing">
                            Problem List / Nursing needs Identified
                        </button>
                    </h2>
                    <div id="problemListNursing" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            {nursingProblems.map((row) => (
                                <div key={row.id} className="border rounded-4 p-3 mb-3 shadow-sm bg-light-subtle">
                                    <div className="row g-3 align-items-center">
                                        <div className="col-md-10">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nursing Need / Problem"
                                                value={row.problem}
                                                onChange={(e) => handleArrayChange("nursingProblems", row.id, "problem", e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-2 text-end">
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger"
                                                onClick={() => removeRow("nursingProblems", row.id)}
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="text-end mt-3">
                                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("nursingProblems")}>
                                    <i className="ri-add-line"></i> Add Nursing Need
                                </button>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Problem List / Nursing needs Identified" />}
                        </div>
                    </div>
                </div>

                {/* Initial Assessment */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#initialAssessment">
                            Initial Assessment
                        </button>
                    </h2>
                    <div id="initialAssessment" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                {/* First row: 2 columns */}
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="nursingStaff"
                                            value={formData.nursingStaff || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Nursing Staff</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="checkedBy"
                                            value={formData.checkedBy || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Checked by</label>
                                    </div>
                                </div>

                                {/* Second row: 2 columns */}
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="designation"
                                            value={formData.designation || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Designation</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="signature"
                                            value={formData.signature || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Signature</label>
                                    </div>
                                </div>

                                {/* Third row: 2 columns */}
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="nursingDate"
                                            value={formData.nursingDate || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Date</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="time"
                                            className="form-control"
                                            name="nursingTime"
                                            value={formData.nursingTime || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Time</label>
                                    </div>
                                </div>

                                {/* Fourth row: 3 columns */}
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="headNurse"
                                            value={formData.headNurse || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Head Nurse</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="clinicalNurseInstructor"
                                            value={formData.clinicalNurseInstructor || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Clinical Nurse Instructor</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="teamLeader"
                                            value={formData.teamLeader || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Team Leader</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Initial Assessment" />}
                        </div>
                    </div>
                </div>

                {/* Monthly Progress Notes and Family Meeting Minutes */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#monthlyProgress">
                            Monthly Progress Notes and Family Meeting Minutes (PRP Section)
                        </button>
                    </h2>
                    <div id="monthlyProgress" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            {/* ===== Read-only Family Meeting Minutes (latest 5, from the FMM form) ===== */}
                            <div className="alert alert-info py-2 d-flex align-items-center gap-2" role="alert">
                                <i className="ri-information-line"></i>
                                <span>These entries are pulled from the <strong>Family Meeting Minutes</strong> form of this case (latest 5, read-only).</span>
                            </div>

                            {fmmNotes.length === 0 ? (
                                <div className="text-center text-muted py-4 border rounded-4 bg-light-subtle">
                                    No Family Meeting Minutes recorded for this case yet.
                                </div>
                            ) : (
                                fmmNotes.map((note, index) => (
                                    <div key={note.id || index} className="border rounded-4 p-3 mb-3 shadow-sm bg-light-subtle">
                                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold px-3 py-1">
                                                Meeting {index + 1}{note.meeting_date ? ` — ${note.meeting_date}` : ""}
                                            </small>
                                            {note.report_written_by && (
                                                <span className="small text-muted">Report Written By: <strong>{note.report_written_by}</strong></span>
                                            )}
                                        </div>
                                        <div className="row g-2 mb-2">
                                            <div className="col-md-6">
                                                <div className="small text-muted">Family Members</div>
                                                <div className="fw-semibold">{(note.family_members || []).map((m) => m.name).filter(Boolean).join(", ") || "—"}</div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="small text-muted">Team Members</div>
                                                <div className="fw-semibold">{(note.team_members || []).map((m) => m.name).filter(Boolean).join(", ") || "—"}</div>
                                            </div>
                                        </div>
                                        <div className="mb-2"><div className="small text-muted">Agenda</div><div>{note.agenda || "—"}</div></div>
                                        <div className="mb-2"><div className="small text-muted">Summary of Discussion</div><div>{note.summary || "—"}</div></div>
                                        <div><div className="small text-muted">Action Plan Agreed Upon</div><div>{note.action_plan || "—"}</div></div>
                                    </div>
                                ))
                            )}

                            {/* Endorsement by Dr. / Additional Remarks */}
                            <div className="accordion-item my-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#endorsement">
                                        Endorsement by Dr. / Additional Remarks
                                    </button>
                                </h2>
                                <div id="endorsement" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control" name="endorsement" value={formData.endorsement || ""} onChange={handleChange} rows="3" />
                                            <label>Endorsement / Remarks</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Monthly Progress Notes and Family Meeting Minutes (PRP Section)" />}
                        </div>
                    </div>

                </div>


                {/* Individual Rehabilitation Plan */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#individualRehabPlan">
                            Individual Rehabilitation Plan
                        </button>
                    </h2>
                    <div id="individualRehabPlan" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="irpName"
                                            value={formData.irpName || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Name</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="irpDate"
                                            value={formData.irpDate || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Date</label>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6 mb-3">
                                    <select
                                        className="form-select"
                                        name="domain"
                                        value={formData.domain || ""}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Domain</option>
                                        <option value="Living">Living</option>
                                        <option value="Learning">Learning</option>
                                        <option value="Working">Working</option>
                                        <option value="Socializing">Socializing</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="rehabGoal"
                                            value={formData.rehabGoal || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Rehab goal #</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2">
                                    Strengths and Skills
                                </small>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="strengths"
                                            value={formData.strengths || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Strengths (skills & resources possess)</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="skillsNeeded"
                                            value={formData.skillsNeeded || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Skills and Resources (needed)</label>
                                    </div>
                                </div>

                                {/* ==== Objectives & Action Plan ==== */}
                                <div className="col-md-12 mb-4">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                                        Objectives & Action Plan
                                    </small>

                                    <div className="accordion" id="objectiveAccordion">
                                        {(formData.objectives || []).map((obj, oIdx) => (
                                            <div className="accordion-item mb-3 shadow-sm" key={obj.id || oIdx}>
                                                <h2 className="accordion-header d-flex align-items-center justify-content-between">
                                                    <button
                                                        className="accordion-button collapsed fw-semibold"
                                                        type="button"
                                                        data-bs-toggle="collapse"
                                                        data-bs-target={`#objective_${obj.id || oIdx}`}
                                                    >
                                                        Objective {oIdx + 1}: {obj.name || "Untitled Objective"}
                                                        <span className="badge bg-label-info ms-3">
                                                            {obj.obj_status}
                                                        </span>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-icon me-3"
                                                        onClick={() => removeObjective(oIdx)}
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                </h2>

                                                <div
                                                    id={`objective_${obj.id || oIdx}`}
                                                    className="accordion-collapse collapse"
                                                >
                                                    <div className="accordion-body">

                                                        {/* ===== Objective Fields ===== */}
                                                        <div className="row g-3 my-4 border rounded-4 p-2 bg-primary-subtle">
                                                            <div className="col-md-6">
                                                                <div className="form-floating">
                                                                    <input
                                                                        className="form-control"
                                                                        placeholder="Objective"
                                                                        value={obj.name}
                                                                        onChange={(e) =>
                                                                            updateObjective(oIdx, "name", e.target.value)
                                                                        }
                                                                    />
                                                                    <label>Objective</label>
                                                                </div>
                                                            </div>

                                                            <div className="col-md-3">
                                                                <select
                                                                    className="form-select"
                                                                    value={obj.obj_status}
                                                                    onChange={(e) =>
                                                                        updateObjective(oIdx, "obj_status", e.target.value)
                                                                    }
                                                                >
                                                                    <option>Pending</option>
                                                                    <option>In Progress</option>
                                                                    <option>Completed</option>
                                                                </select>
                                                            </div>

                                                            <div className="col-md-3">
                                                                <input
                                                                    type="date"
                                                                    className="form-control"
                                                                    value={obj.obj_date}
                                                                    onChange={(e) =>
                                                                        updateObjective(oIdx, "obj_date", e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* ===== Action Steps ===== */}
                                                        {(obj.actions || []).map((act, aIdx) => (
                                                            <div
                                                                key={act.id || aIdx}
                                                                className="row g-2 align-items-center mb-2"
                                                            >
                                                                <div className="col-md-1 fw-semibold">
                                                                    {aIdx + 1}.
                                                                </div>

                                                                <div className="col-md-4">
                                                                    <input
                                                                        className="form-control"
                                                                        placeholder="Action Step"
                                                                        value={act.action_name}
                                                                        onChange={(e) =>
                                                                            updateAction(oIdx, aIdx, "action_name", e.target.value)
                                                                        }
                                                                    />
                                                                </div>

                                                                <div className="col-md-2">
                                                                    <input
                                                                        className="form-control"
                                                                        placeholder="Location"
                                                                        value={act.location}
                                                                        onChange={(e) =>
                                                                            updateAction(oIdx, aIdx, "location", e.target.value)
                                                                        }
                                                                    />
                                                                </div>

                                                                <div className="col-md-2">
                                                                    <input
                                                                        className="form-control"
                                                                        placeholder="Frequency"
                                                                        value={act.frequency}
                                                                        onChange={(e) =>
                                                                            updateAction(oIdx, aIdx, "frequency", e.target.value)
                                                                        }
                                                                    />
                                                                </div>

                                                                <div className="col-md-2">
                                                                    <input
                                                                        className="form-control"
                                                                        placeholder="Duration"
                                                                        value={act.duration}
                                                                        onChange={(e) =>
                                                                            updateAction(oIdx, aIdx, "duration", e.target.value)
                                                                        }
                                                                    />
                                                                </div>

                                                                <div className="col-md-1 text-end">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-lg"
                                                                        onClick={() => removeAction(oIdx, aIdx)}
                                                                    >
                                                                        <i className="ri-close-line"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}

                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary mt-3"
                                                            onClick={() => addAction(oIdx)}
                                                        >
                                                            <i className="ri-add-line"></i> Add Action Step
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-outline-primary mt-3"
                                        onClick={addObjective}
                                    >
                                        <i className="ri-add-line"></i> Add Objective
                                    </button>
                                </div>

                            </div>

                            <p>I have participated in the development of this IRP goal and am in agreement.</p>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="clientSignature"
                                            value={formData.clientSignature || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Client’s Signature</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="clientDate"
                                            value={formData.clientDate || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Date</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="practitionerSignature"
                                            value={formData.practitionerSignature || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Psych Rehab Practitioner’s Signature</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="practitionerDate"
                                            value={formData.practitionerDate || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Date</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Individual Rehabilitation Plan" />}
                        </div>

                    </div>
                </div>

                {/* Discharge Planning */}

                {/* <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#dischargePlanning">
                            Discharge Planning
                        </button>
                    </h2>
                    <div id="dischargePlanning" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">Discharge</small>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="discharge"
                                            value={formData.discharge || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Planned">Planned</option>
                                            <option value="Un planned">Un planned</option>
                                            <option value="Other person">Other person</option>
                                        </select>
                                        <label>Discharge Type</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="dischargeInstructionTo"
                                            value={formData.dischargeInstructionTo || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Client">Client</option>
                                            <option value="Family">Family</option>
                                            <option value="Guardian">Guardian</option>
                                        </select>
                                        <label>Discharge Instructions Given To</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="specialInstructions"
                                            value={formData.specialInstructions || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Any special instructions</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="injections"
                                            value={formData.injections || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Injections If any</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="anyRedFlag"
                                            value={formData.anyRedFlag || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Any Red Flag</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="riskAssessmentDC"
                                            value={formData.riskAssessmentDC || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Risk Assessment at DC</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="expectedDischargeDate"
                                            value={formData.expectedDischargeDate || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Expected date of discharge in consultation with attending physician</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="homeCare"
                                            value={formData.homeCare || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Self">Self</option>
                                            <option value="Family Member">Family Member</option>
                                            <option value="Maid / Servant">Maid / Servant</option>
                                            <option value="Private Nurse">Private Nurse</option>
                                            <option value="Others">Others</option>
                                        </select>
                                        <label>Home Care Provided By</label>
                                    </div>

                                    {formData.homeCare === "Others" && (
                                        <input
                                            type="text"
                                            className="form-control mt-2"
                                            name="homeCareOthersSpecify"
                                            value={formData.homeCareOthersSpecify || ""}
                                            onChange={handleChange}
                                            placeholder="Specify"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="transportation"
                                            value={formData.transportation || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                        <label>Transportation Required</label>
                                    </div>
                                    <p>If Yes inform unity receptionist (UR)</p>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="psychotherapy"
                                            value={formData.psychotherapy || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                        <label>Psychotherapy Required</label>
                                    </div>
                                    <p>If yes arrange appointment.</p>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="longTermCare"
                                            value={formData.longTermCare || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                        <label>Long Term Care Required</label>
                                    </div>
                                    <p>If Yes, arrange facilities for admission in long term care facilities.</p>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="dayRehab"
                                            value={formData.dayRehab || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                        <label>Day Rehabilitation Required</label>
                                    </div>

                                    {formData.dayRehab === "Yes" && (
                                        <div className="form-floating form-floating-outline mt-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="dayRehabDays"
                                                value={formData.dayRehabDays || ""}
                                                onChange={handleChange}
                                            />
                                            <label>Number of days per week</label>
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="otherNeeds"
                                            value={formData.otherNeeds || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                        <label>Any Other Needs</label>
                                    </div>

                                    {formData.otherNeeds === "Yes" && (
                                        <div className="form-floating form-floating-outline mt-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="otherNeedsSpecify"
                                                value={formData.otherNeedsSpecify || ""}
                                                onChange={handleChange}
                                            />
                                            <label>Please Specify Other Needs</label>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Discharge Planning" />}
                        </div>
                    </div>
                </div> */}

                {/* Family Counselling filled by PRP’s / Doctor */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#familyCounselling">
                            Family Counselling filled by PRP’s / Doctor
                        </button>
                    </h2>
                    <div id="familyCounselling" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            {/* Family Education Section */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">Family Education / Guidance</small>
                            <div className="row g-3 mb-3">
                                {[
                                    { name: "educateIllness", label: "Educate the family about the client’s illness condition." },
                                    { name: "educateHygiene", label: "Educate the family about the client’s hygiene at home" },
                                    { name: "educateMedication", label: "Educate the family about the medication and compliance" },
                                    { name: "unusualBehavior", label: "If you see any unusual behavior activity at home they should follow up in OPD" },
                                    { name: "givingMedicine", label: "Educate the family that giving medicine they have to check the mouth of the patient and mentor him/her at least 20 minutes" },
                                    { name: "contactDoctor", label: "Before next follow-up in OPD they should contact Doctor immediately" },
                                    { name: "clientSharing", label: "Client sharing any family should" },
                                    { name: "engagedActivities", label: "Family has to engage the client in different activities, games, watching cartoons, spending time with family." },
                                    { name: "triggerRelapse", label: "Family should trigger sign of relapse (sleep disturbance, drug abuse, resistance to take medicine). Family immediately consults the doctor. If client refused, family should come so medicines can be adjusted and admission can be availed." },
                                ].map((item) => (
                                    <div className="col-md-4" key={item.name}>
                                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block my-3 px-2">{item.label}</small>
                                        <select
                                            className="form-select"
                                            name={item.name}
                                            value={formData[item.name] || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Family Education Section" />}
                        </div>
                    </div>
                </div>

                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#furtherCounselling">
                            Further Counselling.
                        </button>
                    </h2>
                    <div id="furtherCounselling" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            {/* Maintaining regular sleep and wake times. */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#sleepWake">
                                        Maintaining regular sleep and wake times.
                                    </button>
                                </h2>
                                <div id="sleepWake" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control" name="sleepWake" value={formData.sleepWake || ""} onChange={handleChange} rows="3" />
                                            <label>Maintaining regular sleep and wake times.</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Eating balanced meals at regular times. */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#eatingMeals">
                                        Eating balanced meals at regular times.
                                    </button>
                                </h2>
                                <div id="eatingMeals" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control" name="eatingMeals" value={formData.eatingMeals || ""} onChange={handleChange} rows="3" />
                                            <label>Eating balanced meals at regular times.</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Engaging in regular physical activity or exercise. */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#physicalActivity">
                                        Engaging in regular physical activity or exercise.
                                    </button>
                                </h2>
                                <div id="physicalActivity" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control" name="physicalActivity" value={formData.physicalActivity || ""} onChange={handleChange} rows="3" />
                                            <label>Engaging in regular physical activity or exercise.</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Using healthy coping strategies to manage stress. */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#copingStrategies">
                                        Using healthy coping strategies to manage stress.
                                    </button>
                                </h2>
                                <div id="copingStrategies" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control" name="copingStrategies" value={formData.copingStrategies || ""} onChange={handleChange} rows="3" />
                                            <label>Using healthy coping strategies to manage stress.</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Preventing relapse through consistent routines and supports. */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#preventingRelapse">
                                        Preventing relapse through consistent routines and supports.
                                    </button>
                                </h2>
                                <div id="preventingRelapse" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control" name="preventingRelapse" value={formData.preventingRelapse || ""} onChange={handleChange} rows="3" />
                                            <label>Preventing relapse through consistent routines and supports.</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Staying socially connected with family, friends, or community */}
                            <div className="accordion-item mb-3 shadow-sm rounded">
                                <h2 className="accordion-header">
                                    <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#sociallyConnected">
                                        Staying socially connected with family, friends, or community
                                    </button>
                                </h2>
                                <div id="sociallyConnected" className="accordion-collapse collapse">
                                    <div className="accordion-body pt-3">
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control" name="sociallyConnected" value={formData.sociallyConnected || ""} onChange={handleChange} rows="3" />
                                            <label>Staying socially connected with family, friends, or community</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Discharge Planning" />}
                        </div>
                    </div>
                </div>

                {/* Following Information/education has been imparted on the basis of anticipated discharge needs */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#followingInfo">
                            Anticipated Discharge Needs
                        </button>
                    </h2>
                    <div id="followingInfo" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="diet"
                                            value={formData.diet || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Diet</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="takeHomeMedication"
                                            value={formData.takeHomeMedication || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Take Home Medication</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="complianceTreatment"
                                            value={formData.complianceTreatment || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Compliance towards treatment</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="followupVisit"
                                            value={formData.followupVisit || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Followup visit</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="effectiveCoping"
                                            value={formData.effectiveCoping || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Effective Coping</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="hospitalEmergency"
                                            value={formData.hospitalEmergency || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Hospital Emergency</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="activityHome"
                                            value={formData.activityHome || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Activity at Home</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="stressManagement"
                                            value={formData.stressManagement || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Stress Management</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="aggressionManagement"
                                            value={formData.aggressionManagement || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Aggression Management</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="alarmingSigns"
                                            value={formData.alarmingSigns || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Alarming Signs related to disease</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="occupationalTherapy"
                                            value={formData.occupationalTherapy || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Occupational Therapy</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="socialSupport"
                                            value={formData.socialSupport || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Social Support</label>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="environmentalSafety"
                                            value={formData.environmentalSafety || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Environmental Safety</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="processGiverRoleStrain"
                                            value={formData.processGiverRoleStrain || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Processgiver Role Strain</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="longTermFacilities"
                                            value={formData.longTermFacilities || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Long Term Facilities</label>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="contactNumber"
                                            value={formData.contactNumber || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Contact#</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="others"
                                            value={formData.others || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Others</label>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="nameSignature"
                                            value={formData.nameSignature || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Name / Signature</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="signatureDate"
                                            value={formData.signatureDate || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Date</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="time"
                                            className="form-control"
                                            name="signatureTime"
                                            value={formData.signatureTime || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Time</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Anticipated Discharge Needs" />}
                        </div>

                    </div>
                </div>

                {/* Home Care Future Support Plan */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#homeCarePlan">
                            Home Care Future Support Plan
                        </button>
                    </h2>
                    <div id="homeCarePlan" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="possibleRelapseSign"
                                            value={formData.possibleRelapseSign || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Possible relapse sign</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="recoveryGoals"
                                            value={formData.recoveryGoals || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Recovery goals</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="whoToContact"
                                            value={formData.whoToContact || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Who to contact</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="whereToGoCrises"
                                            value={formData.whereToGoCrises || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Where to go in crises</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="detailsMedication"
                                            value={formData.detailsMedication || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Details of Medication</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="detailTreatmentPlan"
                                            value={formData.detailTreatmentPlan || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Detail of comprehensive treatment plan</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="physicalHealthNeeds"
                                            value={formData.physicalHealthNeeds || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Physical health needs</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="admissionPathway"
                                            value={formData.admissionPathway || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Admission pathway</label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="riskAssessmentSafetyPlan"
                                            value={formData.riskAssessmentSafetyPlan || ""}
                                            onChange={handleChange}
                                            rows="3"
                                        />
                                        <label>Risk assessment and safety plan</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="dateOfReview"
                                            value={formData.dateOfReview || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Date of review</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="doctorName"
                                            value={formData.doctorName || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Doctor Name</label>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="guardianName"
                                            value={formData.guardianName || ""}
                                            onChange={handleChange}
                                        />
                                        <label>Guardian / Client Name</label>
                                    </div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Home Care Future Support Plan" />}
                        </div>

                    </div>
                </div>
                {/* end Home Care Future Support Plan */}

                {/* ===================================================== */}
                {/* Discharge Planning (Updated)  (own accordion-item)    */}
                {/* ===================================================== */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#dischargePlanningHomeCare">
                            Discharge Planning (Updated)
                        </button>
                    </h2>
                    <div id="dischargePlanningHomeCare" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="mb-3">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Education Provided To</small>
                                <div className="d-flex flex-wrap gap-3">
                                    {["Client", "Family", "Guardian"].map((opt) => (
                                        <div className="form-check form-check-inline" key={opt}>
                                            <input className="form-check-input" type="checkbox" id={`dp_edu_${opt}`} checked={csvHas(dp.education_provided_to, opt)} onChange={() => toggleCsv(dp, setDp, "education_provided_to", opt)} />
                                            <label className="form-check-label" htmlFor={`dp_edu_${opt}`}>{opt}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control" style={{ height: "80px" }} value={dp.recovery_goals || ""} onChange={(e) => setDp("recovery_goals", e.target.value)}></textarea>
                                        <label>Recovery Goals</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control" style={{ height: "80px" }} value={dp.possible_relapse_signs || ""} onChange={(e) => setDp("possible_relapse_signs", e.target.value)}></textarea>
                                        <label>Possible Relapse Signs</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" value={dp.who_to_contact || ""} onChange={(e) => setDp("who_to_contact", e.target.value)} />
                                        <label>Who to Contact</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" value={dp.where_to_go_crisis || ""} onChange={(e) => setDp("where_to_go_crisis", e.target.value)} />
                                        <label>Where to Go in Crisis</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control" style={{ height: "70px" }} value={dp.red_flag_warnings || ""} onChange={(e) => setDp("red_flag_warnings", e.target.value)}></textarea>
                                        <label>Red Flag / Warnings</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="date" className="form-control" value={dp.followup_psychiatrist_date || ""} onChange={(e) => setDp("followup_psychiatrist_date", e.target.value)} />
                                        <label>Follow-up Psychiatrist Appointment</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" value={dp.physical_health_needs || ""} onChange={(e) => setDp("physical_health_needs", e.target.value)} />
                                        <label>Referred For Physical Health Needs — Other (specify)</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" value={dp.special_instructions || ""} onChange={(e) => setDp("special_instructions", e.target.value)} />
                                        <label>Special Instructions</label>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Transition For</small>
                                <div className="d-flex flex-wrap gap-3">
                                    {["Day Rehab", "Home Visitation", "Vocational Activities", "Other"].map((opt) => (
                                        <div className="form-check form-check-inline" key={opt}>
                                            <input className="form-check-input" type="checkbox" id={`dp_trans_${opt}`} checked={csvHas(dp.transition_for, opt)} onChange={() => toggleCsv(dp, setDp, "transition_for", opt)} />
                                            <label className="form-check-label" htmlFor={`dp_trans_${opt}`}>{opt}</label>
                                        </div>
                                    ))}
                                </div>
                                {csvHas(dp.transition_for, "Other") && (
                                    <input type="text" className="form-control mt-2" placeholder="Transition — Other (specify)" value={dp.transition_for_other || ""} onChange={(e) => setDp("transition_for_other", e.target.value)} />
                                )}
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Discharge Planning" />}
                        </div>
                    </div>
                </div>

                {/* ===================================================== */}
                {/* Cognitive Assessment (with sub-accordions)            */}
                {/* ===================================================== */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cognitiveAssessment">
                            Cognitive Assessment
                        </button>
                    </h2>
                    <div id="cognitiveAssessment" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="accordion" id="cognitiveAssessmentAccordion">

                                {/* 1. Patient Info & Setting */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub1">1. Auto-Populated Patient Information</button></h2>
                                    <div id="cogSub1" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        <div className="row g-2 mb-2 small text-muted">
                                            <div className="col-md-4">Patient ID: <strong>{formData.mrNumber || "—"}</strong></div>
                                            <div className="col-md-4">Name: <strong>{formData.name || "—"}</strong></div>
                                            <div className="col-md-4">Age / Gender: <strong>{formData.age || "—"} / {formData.gender || "—"}</strong></div>
                                            <div className="col-md-4">Date: <strong>{formData.date || "—"}</strong></div>
                                        </div>
                                        {cogRow("Setting", "setting", ["OPD", "IPD", "ER", "Rehab"])}
                                    </div></div>
                                </div>

                                {/* 2. Level of Consciousness */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub2">2. Level of Consciousness</button></h2>
                                    <div id="cogSub2" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogOpts("level_of_consciousness", ["Alert", "Drowsy", "Stuporous", "Comatose", "Fluctuating"])}
                                    </div></div>
                                </div>

                                {/* 3. Orientation */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub3">3. Orientation <span className="badge bg-label-primary ms-2">Score: {cog.orientation_score ?? 0}/3</span></button></h2>
                                    <div id="cogSub3" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogRow("Time", "orientation_time", ["Intact", "Impaired"])}
                                        {cogRow("Place", "orientation_place", ["Intact", "Impaired"])}
                                        {cogRow("Person", "orientation_person", ["Intact", "Impaired"])}
                                        <div className="text-muted small">🔢 Auto-score: {cog.orientation_score ?? 0} / 3 (1 point each correct)</div>
                                    </div></div>
                                </div>

                                {/* 4. Attention & Concentration */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub4">4. Attention &amp; Concentration <span className="badge bg-label-primary ms-2">Score: {cog.attention_score ?? 0}/5</span></button></h2>
                                    <div id="cogSub4" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogRow("Digit Span Forward", "digit_span_forward", ["Normal", "Impaired"])}
                                        {cogRow("Digit Span Backward", "digit_span_backward", ["Normal", "Impaired"])}
                                        {cogRow("Serial 7s", "serial_7s", ["Completed", "Partial", "Unable"])}
                                        {cogRow("Months Backward", "months_backward", ["Completed", "Unable"])}
                                        {cogRow("Distractibility", "distractibility", ["Present", "Absent"])}
                                        <div className="text-muted small">🔢 Auto-score: {cog.attention_score ?? 0} / 5</div>
                                    </div></div>
                                </div>

                                {/* 5. Memory */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub5">5. Memory Assessment <span className="badge bg-label-primary ms-2">Score: {cog.memory_score ?? 0}/10</span></button></h2>
                                    <div id="cogSub5" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        <small className="fw-bold d-block mb-1">Immediate Memory</small>
                                        {cogRow("3-Object Recall (Immediate)", "recall_3_object_immediate", ["0", "1", "2", "3"])}
                                        <small className="fw-bold d-block mb-1 mt-2">Recent Memory</small>
                                        {cogRow("5-Min Recall", "recall_5_min", ["0", "1", "2", "3"])}
                                        {cogRow("Recent Events Recall", "recent_events_recall", ["Intact", "Impaired"])}
                                        <small className="fw-bold d-block mb-1 mt-2">Remote Memory</small>
                                        {cogRow("Personal History", "personal_history", ["Intact", "Impaired"])}
                                        {cogRow("Past Events", "past_events", ["Intact", "Impaired"])}
                                        <div className="text-muted small">🔢 Auto-score: {cog.memory_score ?? 0} / 10</div>
                                    </div></div>
                                </div>

                                {/* 6. Language */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub6">6. Language Function <span className="badge bg-label-primary ms-2">Score: {cog.language_score ?? 0}/6</span></button></h2>
                                    <div id="cogSub6" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogRow("Fluency", "fluency", ["Normal", "Reduced", "Pressured"])}
                                        {cogRow("Comprehension", "comprehension", ["Intact", "Impaired"])}
                                        {cogRow("Naming", "naming", ["Intact", "Impaired"])}
                                        {cogRow("Repetition", "repetition", ["Intact", "Impaired"])}
                                        {cogRow("Reading", "reading", ["Intact", "Impaired"])}
                                        {cogRow("Writing", "writing", ["Intact", "Impaired"])}
                                        <div className="text-muted small">🔢 Auto-score: {cog.language_score ?? 0} / 6</div>
                                    </div></div>
                                </div>

                                {/* 7. Executive */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub7">7. Executive Functions <span className="badge bg-label-primary ms-2">Score: {cog.executive_score ?? 0}/5</span></button></h2>
                                    <div id="cogSub7" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        <small className="fw-bold d-block mb-1">Abstract Thinking</small>
                                        {cogRow("Proverbs", "proverbs", ["Abstract", "Concrete"])}
                                        {cogRow("Similarities", "similarities", ["Intact", "Impaired"])}
                                        <small className="fw-bold d-block mb-1 mt-2">Judgment</small>
                                        {cogRow("Social Judgment", "social_judgment", ["Intact", "Impaired"])}
                                        {cogRow("Test Judgment", "test_judgment", ["Intact", "Impaired"])}
                                        <small className="fw-bold d-block mb-1 mt-2">Planning</small>
                                        {cogRow("Task Planning", "task_planning", ["Intact", "Impaired"])}
                                        <div className="text-muted small">🔢 Auto-score: {cog.executive_score ?? 0} / 5</div>
                                    </div></div>
                                </div>

                                {/* 8. Perception */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub8">8. Perception</button></h2>
                                    <div id="cogSub8" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogOpts("perception", ["Normal", "Illusions", "Hallucinations"])}
                                        <div className="mt-2">
                                            <small className="fw-bold d-block mb-1">Type</small>
                                            <div className="d-flex flex-wrap gap-3">
                                                {["Auditory", "Visual", "Tactile", "Olfactory"].map((opt) => (
                                                    <div className="form-check form-check-inline" key={opt}>
                                                        <input className="form-check-input" type="checkbox" id={`cog_perc_${opt}`} checked={csvHas(cog.perception_type, opt)} onChange={() => toggleCsv(cog, setCog, "perception_type", opt)} />
                                                        <label className="form-check-label" htmlFor={`cog_perc_${opt}`}>{opt}</label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div></div>
                                </div>

                                {/* 9. Visuospatial */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub9">9. Visuospatial <span className="badge bg-label-primary ms-2">Score: {cog.visuospatial_score ?? 0}/3</span></button></h2>
                                    <div id="cogSub9" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogRow("Shape Copying", "shape_copying", ["Normal", "Impaired"])}
                                        {cogRow("Clock Drawing", "clock_drawing", ["Normal", "Impaired"])}
                                        {cogRow("Spatial Orientation", "spatial_orientation", ["Intact", "Impaired"])}
                                        <div className="text-muted small">🔢 Auto-score: {cog.visuospatial_score ?? 0} / 3</div>
                                    </div></div>
                                </div>

                                {/* 10. Psychomotor */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub10">10. Psychomotor Activity</button></h2>
                                    <div id="cogSub10" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogOpts("psychomotor", ["Normal", "Agitation", "Retardation", "Abnormal Movements"])}
                                    </div></div>
                                </div>

                                {/* 11. Insight */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub11">11. Insight</button></h2>
                                    <div id="cogSub11" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogOpts("insight", ["Full", "Partial", "None"])}
                                    </div></div>
                                </div>

                                {/* 12. Risk Indicators */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub12">12. Risk Indicators {riskCount >= 2 && <span className="badge bg-danger ms-2">🚨 High Risk Cognitive Profile</span>}</button></h2>
                                    <div id="cogSub12" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        <div className="row g-2">
                                            {[
                                                ["risk_impulsivity", "Impulsivity"],
                                                ["risk_poor_judgment", "Poor Judgment"],
                                                ["risk_wandering", "Wandering"],
                                                ["risk_confusion", "Confusion Risk"],
                                                ["risk_non_adherence", "Non-adherence"],
                                            ].map(([field, label]) => (
                                                <div className="col-md-4" key={field}>
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="checkbox" id={`cog_${field}`} checked={!!cog[field]} onChange={(e) => setCog(field, e.target.checked)} />
                                                        <label className="form-check-label" htmlFor={`cog_${field}`}>{label}</label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="text-muted small mt-2">🚨 Auto Alert: If ≥2 checked → "High Risk Cognitive Profile" {riskCount >= 2 ? "(TRIGGERED)" : ""}</div>
                                    </div></div>
                                </div>

                                {/* 13. Functional Status */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub13">13. Functional Status</button></h2>
                                    <div id="cogSub13" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        {cogOpts("functional_status", ["Independent", "Needs Supervision", "Dependent"])}
                                    </div></div>
                                </div>

                                {/* 14. Overall Cognitive Score */}
                                <div className="accordion-item mb-2">
                                    <h2 className="accordion-header"><button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cogSub14">14. Overall Cognitive Score <span className="badge bg-primary ms-2">{cog.total_score ?? 0}/32</span></button></h2>
                                    <div id="cogSub14" className="accordion-collapse collapse"><div className="accordion-body pt-3">
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-sm align-middle mb-2">
                                                <tbody>
                                                    <tr><td>Orientation</td><td className="text-center">{cog.orientation_score ?? 0} / 3</td></tr>
                                                    <tr><td>Attention</td><td className="text-center">{cog.attention_score ?? 0} / 5</td></tr>
                                                    <tr><td>Memory</td><td className="text-center">{cog.memory_score ?? 0} / 10</td></tr>
                                                    <tr><td>Language</td><td className="text-center">{cog.language_score ?? 0} / 6</td></tr>
                                                    <tr><td>Executive</td><td className="text-center">{cog.executive_score ?? 0} / 5</td></tr>
                                                    <tr><td>Visuospatial</td><td className="text-center">{cog.visuospatial_score ?? 0} / 3</td></tr>
                                                    <tr className="table-primary fw-bold"><td>Total Score</td><td className="text-center">{cog.total_score ?? 0} / 32</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="alert alert-info mb-0 py-2">
                                            <strong>Interpretation:</strong> {cog.interpretation || "—"}
                                            <div className="small text-muted mt-1">28–32 Normal · 22–27 Mild · 15–21 Moderate · &lt;15 Severe</div>
                                        </div>
                                    </div></div>
                                </div>
                            </div>
                            {onSectionSave && <SectionSaveBtn sectionName="Cognitive Assessment" />}
                        </div>
                    </div>
                </div>
            </div>




            {/* Footer */}
            <div className="row g-3 mt-4">
                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="nameOfDocuments"
                            value={formData.nameOfDocuments || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Name of Documents"
                            readOnly
                        />
                        <label>Name of Documents</label>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="effectiveDate"
                            value={formData.effectiveDate || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Effective Date</label>
                    </div>
                </div>
            </div>

            <div className="row g-3 mt-3">
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="approvedBy"
                            value={formData.approvedBy || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Approved By</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="approvedDate"
                            value={formData.approvedDate || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Date</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="nextRevisionBy"
                            value={formData.nextRevisionBy || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Next Revision By</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="nextRevisionDate"
                            value={formData.nextRevisionDate || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Date</label>
                    </div>
                </div>
            </div>
        </div >

    );
};

export default IntegratedInitialAssessmentForm;